import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../firebase/config';
import { collection, addDoc, getDocs, doc, getDoc, updateDoc, deleteDoc, query, where, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import Toast from '../components/Toast';
import '../styles/mobile.css';

const DEFAULT_PROFILE_IMAGE = 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_960_720.png';

const validateAndLoadImage = async (url) => {
  if (!url) return DEFAULT_PROFILE_IMAGE;
  try {
    const response = await fetch(url);
    if (!response.ok) return DEFAULT_PROFILE_IMAGE;
    return url;
  } catch {
    return DEFAULT_PROFILE_IMAGE;
  }
};

const isMobile = () => window.innerWidth < 768;
const getResponsiveSize = (mobileSize, desktopSize) => isMobile() ? mobileSize : desktopSize;

const CATEGORIAS_PRODUCTOS = [
  'Postres',
  'Bebidas',
  'Ensaladas de frutas',
  'Helados',
  'Cholados y raspados',
  'Café',
  'Comida rápida',
  'Adiciones'
];

const SUCURSALES_DISPONIBLES = [
  'Local sede C',
  'Local sede F',
  'Local el recuerdo',
];

// Configuración de comisiones interlócales
const CONFIGURACION_COMISIONES = {
  'Local sede C': {
    pagaComisionA: ['Local sede F'],
    recibeComisionDe: ['Local sede F'],
    porcentajeComision: 0.10
  },
  'Local sede F': {
    pagaComisionA: ['Local sede C'],
    recibeComisionDe: ['Local sede C'],
    porcentajeComision: 0.10
  },
  'Local el recuerdo': {
    pagaComisionA: [],
    recibeComisionDe: [],
    porcentajeComision: 0
  }
};

// Mapeo de sucursales a categorías principales
const SUCURSAL_CATEGORIAS = {
  'Local sede C': ['Postres', 'Helados', 'Cholados y raspados'],
  'Local sede F': ['Comida rápida', 'Ensaladas de frutas'],
  'Local el recuerdo': [] // Vende de todo
};

// Array de nombres de sucursales para los selectores
const NOMBRES_SUCURSALES = ['Local sede C', 'Local sede F', 'Local el recuerdo'];

function DashboardAdmin() {
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState({
    nombre: '',
    rol: '',
    imagenURL: 'https://via.placeholder.com/50'
  });
  const [adminProfile] = useState({
    nombre: 'Admin',
    imagenURL: 'https://via.placeholder.com/50',  // placeholder image, replace with actual admin image
  });
  const [productos, setProductos] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [nuevoProducto, setNuevoProducto] = useState({ 
    nombre: '', 
    cantidad: '', 
    precio: '', 
    imagen: null,
    imagenURL: '',
    categoria: CATEGORIAS_PRODUCTOS[0], // Categoría por defecto
    sucursalOrigen: SUCURSALES_DISPONIBLES[0] // Sucursal de origen por defecto
  });
  const [nuevoVendedor, setNuevoVendedor] = useState({
    username: '',
    password: '',
    rol: 'vendedor',
    imagen: null, // Archivo de imagen para procesar
    imagenURL: DEFAULT_PROFILE_IMAGE, // URL de imagen para mostrar
    sucursal: SUCURSALES_DISPONIBLES[0] // Sucursal por defecto
  });
  const [editandoProducto, setEditandoProducto] = useState(null);
  const [editandoVendedor, setEditandoVendedor] = useState(null);
  const [seccionActiva, setSeccionActiva] = useState('inventario');
  const [menuMovilAbierto, setMenuMovilAbierto] = useState(false);
  const [ventasDelDia, setVentasDelDia] = useState([]);
  const [totalDelDia, setTotalDelDia] = useState(0);
  const [topProductos, setTopProductos] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [dateRange, setDateRange] = useState({
    start: new Date().toISOString().split('T')[0], // Día actual
    end: new Date().toISOString().split('T')[0]    // Día actual
  });
  const [metodoPagoStats, setMetodoPagoStats] = useState({
    efectivo: 0,
    tarjeta: 0,
    transferencia: 0
  });
  // Añadir estado para filtro de categoría
  const [filtroCategoria, setFiltroCategoria] = useState('Todas');

  // Añadir estado para filtro de sucursal
  const [filtroSucursal, setFiltroSucursal] = useState('Todas');

  // Añadir estado para las ventas por método de pago
  const [ventasPorMetodoPago, setVentasPorMetodoPago] = useState({
    efectivo: [],
    tarjeta: [],
    transferencia: []
  });
  
  // Estado para los mensajes Toast
  const [toast, setToast] = useState({
    visible: false,
    message: '',
    type: 'success'
  });

  // Estados para gestión de categorías
  const [categorias, setCategorias] = useState(CATEGORIAS_PRODUCTOS);
  const [nuevaCategoria, setNuevaCategoria] = useState('');
  const [editandoCategoria, setEditandoCategoria] = useState(null);
  const [cargandoCategorias, setCargandoCategorias] = useState(false);

  // Estados para gestión de mesas
  const [mesasPorSucursal, setMesasPorSucursal] = useState({});
  const [nuevaMesa, setNuevaMesa] = useState({
    sucursal: SUCURSALES_DISPONIBLES[0],
    numero: '',
    capacidad: '',
    estado: 'disponible'
  });
  const [editandoMesa, setEditandoMesa] = useState(null);

  // Estados para gestión de ventas interlócales
  const [ventasInterlocales, setVentasInterlocales] = useState([]);
  const [comisionesPendientes, setComisionesPendientes] = useState([]);
  const [reporteComisiones, setReporteComisiones] = useState({});

  // Función para mostrar mensajes
  const showToast = (message, type = 'success') => {
    setToast({
      visible: true,
      message,
      type
    });
  };

  // Función para cerrar el toast
  const closeToast = () => {
    setToast({ ...toast, visible: false });
  };
  
  // Cargar perfil de usuario
  useEffect(() => {
    const cargarPerfilUsuario = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        if (!userData) {
          navigate('/');
          return;
        }

        // Cargar perfil del usuario
        const userQuery = query(
          collection(db, "vendedores"), 
          where("username", "==", userData.username)
        );
        const userSnapshot = await getDocs(userQuery);
        
        if (!userSnapshot.empty) {
          const userInfo = userSnapshot.docs[0].data();
          setUserProfile({
            nombre: userInfo.username,
            rol: userInfo.rol,
            imagenURL: userInfo.imagenURL || DEFAULT_PROFILE_IMAGE
          });
          
          // Actualizar el localStorage con la imagen actual
          const updatedUserData = {
            ...userData,
            imagenURL: userInfo.imagenURL
          };
          localStorage.setItem('user', JSON.stringify(updatedUserData));
        }
      } catch (error) {
        console.error("Error al cargar perfil:", error);
        setUserProfile(prev => ({...prev, imagenURL: DEFAULT_PROFILE_IMAGE}));
      }
    };
    cargarPerfilUsuario();
  }, [navigate]);

  // Cargar comisiones al inicio de la aplicación
  useEffect(() => {
    const cargarDatosIniciales = async () => {
      try {
        console.log("🚀 Cargando datos iniciales de comisiones...");
        
        // Cargar todas las ventas interlócales sin filtro de fecha inicial
        const ventasQuery = query(collection(db, "ventas_interlocales"));
        const ventasSnapshot = await getDocs(ventasQuery);
        const ventasData = ventasSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          fecha: doc.data().fecha.toDate()
        }));

        console.log("📊 Total de ventas interlócales en DB:", ventasData.length, ventasData);
        
        setVentasInterlocales(ventasData);
        calcularComisionesPendientes(ventasData);
        
        if (ventasData.length > 0) {
          showToast(`Se cargaron ${ventasData.length} transacciones de comisiones`, 'success');
        } else {
          console.log("⚠️ No se encontraron ventas interlócales en la base de datos");
          showToast("No hay transacciones de comisiones registradas", 'info');
        }
        
        console.log("✅ Datos de comisiones cargados al inicio");
      } catch (error) {
        console.error("❌ Error al cargar datos iniciales de comisiones:", error);
        showToast("Error al cargar comisiones: " + error.message, 'error');
      }
    };
    cargarDatosIniciales();
  }, []);

  // Función para manejar la selección de archivo de imagen (Base64)
  const handleVendedorImageChange = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo de archivo
      if (!file.type.startsWith('image/')) {
        showToast("Por favor selecciona un archivo de imagen válido", 'error');
        return;
      }
      
      // Validar tamaño del archivo (max 2MB para Base64)
      if (file.size > 2 * 1024 * 1024) {
        showToast("La imagen debe ser menor a 2MB para un mejor rendimiento", 'error');
        return;
      }

      // Convertir a Base64
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result; // Ya incluye el prefijo data:image/...;base64,
        
        if (isEdit) {
          setEditandoVendedor(prev => ({
            ...prev,
            imagen: file,
            imagenURL: base64 // Usar Base64 como URL
          }));
        } else {
          setNuevoVendedor(prev => ({
            ...prev,
            imagen: file,
            imagenURL: base64 // Usar Base64 como URL
          }));
        }
        
        showToast("Imagen cargada correctamente", 'success');
      };
      
      reader.onerror = () => {
        showToast("Error al procesar la imagen", 'error');
      };
      
      reader.readAsDataURL(file); // Esto genera el Base64
    }
  };

  // Función para comprimir imagen Base64 (opcional, para mejor rendimiento)
  const comprimirImagenBase64 = (base64, maxWidth = 200, maxHeight = 200, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calcular nuevas dimensiones manteniendo proporción
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Dibujar y comprimir
        ctx.drawImage(img, 0, 0, width, height);
        const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedBase64);
      };
      
      img.src = base64;
    });
  };

  // Modificar función agregarProducto
  const agregarProducto = async (e) => {
    e.preventDefault();
    console.log("Intentando agregar producto:", nuevoProducto);
    
    // Validaciones básicas
    if (!nuevoProducto.nombre || nuevoProducto.nombre.trim() === '') {
      showToast("El nombre del producto es obligatorio", 'error');
      return;
    }
    
    if (!nuevoProducto.precio || isNaN(parseFloat(nuevoProducto.precio))) {
      showToast("El precio debe ser un número válido", 'error');
      return;
    }
    
    try {
      // Check if product name already exists
      const productoQuery = query(
        collection(db, "productos"), 
        where("nombre", "==", nuevoProducto.nombre)
      );
      const productoSnapshot = await getDocs(productoQuery);

      if (!productoSnapshot.empty) {
        showToast("Ya existe un producto con ese nombre. Por favor elija otro nombre.", 'error');
        return;
      }

      // Mostrar mensaje de carga
      showToast("Procesando producto...", 'info');
      
      let imagenURL = '';

      // Si hay una nueva imagen archivo, subirla a Firebase Storage
      if (nuevoProducto.imagen && typeof nuevoProducto.imagen === 'object') {
        try {
          console.log("Comprobando tamaño de la imagen:", nuevoProducto.imagen.size, "bytes");
          // Limitar tamaño a 2MB para mayor compatibilidad con Firebase Storage
          if (nuevoProducto.imagen.size > 2 * 1024 * 1024) {
            showToast("La imagen es demasiado grande (máximo 2MB). Por favor, comprime la imagen e intenta nuevamente.", 'error');
            return;
          }

          showToast("Subiendo imagen... Espere por favor.", 'info');
          console.log("Subiendo imagen a Firebase Storage:", nuevoProducto.imagen.name);
          
          // Generar un nombre de archivo único con timestamp para evitar colisiones
          const timestamp = new Date().getTime();
          // Eliminar espacios y caracteres especiales del nombre del archivo
          const nombreLimpio = nuevoProducto.imagen.name.replace(/[^a-zA-Z0-9.]/g, '_');
          const nombreArchivo = `${timestamp}_${nombreLimpio}`;
          
          // Crear referencia a Firebase Storage
          const imageRef = ref(storage, `productos/${nombreArchivo}`);
          
          // Verificar el tamaño una vez más antes de subir
          if (nuevoProducto.imagen.size > 2 * 1024 * 1024) {
            showToast(`Imagen demasiado grande: ${Math.round(nuevoProducto.imagen.size/1024)}KB. Intentando comprimir más...`, "warning");
            
            try {
              // Intentar comprimir aún más
              const imagenMasComprimida = await comprimirImagen(nuevoProducto.imagen, 0.3);
              if (imagenMasComprimida.size < nuevoProducto.imagen.size) {
                showToast(`Compresión adicional aplicada: ${Math.round(imagenMasComprimida.size/1024)}KB`, "info");
                nuevoProducto.imagen = imagenMasComprimida;
              }
            } catch (compressError) {
              console.error("Error en compresión adicional:", compressError);
            }
          }
          
          // Subir la imagen con un tiempo límite mayor (60 segundos)
          showToast("Subiendo imagen... Por favor, espere y no cierre la página.", "info");
          const uploadPromise = uploadBytes(imageRef, nuevoProducto.imagen);
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error("Tiempo de espera agotado durante la subida")), 60000) // 60 segundos
          );
          
          console.log(`Iniciando subida de imagen (${Math.round(nuevoProducto.imagen.size/1024)}KB)...`);
          const uploadResult = await Promise.race([uploadPromise, timeoutPromise]);
          console.log("Imagen subida con éxito:", uploadResult);
          
          // Obtener URL de descarga
          imagenURL = await getDownloadURL(imageRef);
          console.log("URL de imagen obtenida:", imagenURL);
          
          if (!imagenURL) {
            throw new Error("No se pudo obtener la URL de la imagen");
          }
          
        } catch (uploadError) {
          console.error("Error detallado al subir la imagen:", uploadError);
          
          // Intentar solución alternativa: convertir imagen a base64 para almacenamiento temporal
          try {
            showToast("Intentando método alternativo para guardar la imagen...", 'info');
            
            // Obtener tamaño de la imagen
            const sizeKB = Math.round(nuevoProducto.imagen.size/1024);
            
            // Si es demasiado grande para base64 (más de 100KB), no usar este método
            if (sizeKB > 100) {
              showToast(`La imagen es demasiado grande (${sizeKB}KB) para el método alternativo. Intente con una más pequeña.`, 'error');
              return;
            }
            
            // Leer como base64
            const reader = new FileReader();
            reader.readAsDataURL(nuevoProducto.imagen);
            
            reader.onload = async () => {
              try {
                // Usar la cadena base64 como URL de imagen
                imagenURL = reader.result;
                
                // Continuar con el resto del proceso
                const productoData = {
                  nombre: nuevoProducto.nombre,
                  cantidad: nuevoProducto.cantidad ? parseInt(nuevoProducto.cantidad) : null,
                  precio: parseFloat(nuevoProducto.precio),
                  imagenURL: imagenURL, // Usar base64
                  categoria: nuevoProducto.categoria,
                  sucursalOrigen: nuevoProducto.sucursalOrigen,
                  fechaCreacion: new Date(),
                  imagenEsBase64: true // Marcar que es base64 para futura referencia
                };
                
                console.log("Guardando producto con imagen en base64");
                
                const docRef = await addDoc(collection(db, "productos"), productoData);
                
                // Actualizar estado local
                setProductos([...productos, { 
                  id: docRef.id, 
                  ...productoData
                }]);
                
                // Limpiar formulario
                setNuevoProducto({ 
                  nombre: '', 
                  cantidad: '', 
                  precio: '', 
                  imagen: null,
                  imagenURL: '',
                  categoria: categorias[0] || 'Postres',
                  sucursalOrigen: NOMBRES_SUCURSALES[0]
                });
                
                showToast("Producto guardado con método alternativo. Las imágenes pueden ser de menor calidad.", 'success');
              } catch (finalError) {
                console.error("Error en método alternativo:", finalError);
                showToast("No se pudo guardar el producto: " + finalError.message, 'error');
              }
            };
            
            reader.onerror = () => {
              showToast("Error al procesar la imagen con método alternativo", 'error');
            };
            
            // No continuar con el flujo normal
            return true;
          } catch (alternativeError) {
            console.error("Error en método alternativo:", alternativeError);
            showToast(`Error al subir la imagen: ${uploadError.message}. Intente con una imagen más pequeña.`, 'error');
            return; // Detener el proceso sin lanzar excepción para mantener los datos del formulario
          }
        }
      } else if (nuevoProducto.imagenURL && !nuevoProducto.imagenURL.startsWith('blob:')) {
        // Si hay una URL externa (no blob), usarla
        imagenURL = nuevoProducto.imagenURL;
      }

      const productoData = {
        nombre: nuevoProducto.nombre,
        cantidad: nuevoProducto.cantidad ? parseInt(nuevoProducto.cantidad) : null,
        precio: parseFloat(nuevoProducto.precio),
        imagenURL: imagenURL, // Usar la URL de la imagen subida
        categoria: nuevoProducto.categoria, // Agregar categoría
        sucursalOrigen: nuevoProducto.sucursalOrigen, // Agregar sucursal de origen
        fechaCreacion: new Date()
      };

      console.log("Datos a guardar:", productoData);
      
      const docRef = await addDoc(collection(db, "productos"), productoData);
      console.log("Documento guardado con ID:", docRef.id);

      // Actualizar estado local
      setProductos([...productos, { 
        id: docRef.id, 
        ...productoData
      }]);

      // Limpiar formulario
      setNuevoProducto({ 
        nombre: '', 
        cantidad: '', 
        precio: '', 
        imagen: null,
        imagenURL: '',
        categoria: categorias[0] || 'Postres', // Mantener categoría por defecto
        sucursalOrigen: NOMBRES_SUCURSALES[0] // Mantener sucursal por defecto
      });

      showToast("¡Producto agregado exitosamente!", 'success');
    } catch (error) {
      console.error("Error detallado al agregar producto:", error);
      
      // Mostrar un mensaje de error más descriptivo según el tipo de error
      if (error.code === 'storage/unauthorized') {
        showToast("Error de permisos: No tienes autorización para subir imágenes.", 'error');
      } else if (error.code === 'storage/canceled') {
        showToast("La subida de la imagen fue cancelada.", 'error');
      } else if (error.code === 'storage/retry-limit-exceeded') {
        showToast("Error de red: Comprueba tu conexión a internet e intenta de nuevo.", 'error');
      } else if (error.code && error.code.startsWith('storage/')) {
        showToast(`Error de almacenamiento: ${error.message}`, 'error');
      } else {
        showToast(`Error al agregar producto: ${error.message}`, 'error');
      }
      
      // No limpiar el formulario en caso de error para que el usuario pueda intentar de nuevo
    }
  };
  
  // Función para cargar productos
  const cargarProductos = async () => {
    try {
      console.log("Cargando productos...");
      const querySnapshot = await getDocs(collection(db, "productos"));
      const productosDB = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log("Productos cargados:", productosDB);
      setProductos(productosDB);
    } catch (error) {
      console.error("Error al cargar productos:", error);
    }
  };

  // Cargar productos al inicio
  useEffect(() => {
    cargarProductos();
  }, []);

  // Cargar vendedores al inicio
  useEffect(() => {
    const cargarVendedores = async () => {
      try {
        console.log("📥 Cargando vendedores desde Firestore...");
        const querySnapshot = await getDocs(collection(db, "vendedores"));
        const vendedoresDB = querySnapshot.docs.map(doc => {
          const data = doc.data();
          const vendedor = {
            id: doc.id,
            ...data,
            // Asegurar que todos los vendedores tengan una imagen
            imagenURL: data.imagenURL || DEFAULT_PROFILE_IMAGE
          };
          console.log("👤 Vendedor cargado:", vendedor);
          return vendedor;
        });
        setVendedores(vendedoresDB);
        console.log("✅ Total vendedores cargados:", vendedoresDB.length);
      } catch (error) {
        console.error("❌ Error al cargar vendedores:", error);
        showToast("Error al cargar vendedores: " + error.message, 'error');
      }
    };
    cargarVendedores();
  }, []);

  // Cargar ventas del día
  useEffect(() => {
    const cargarVentas = async () => {
      try {
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        const ventasQuery = query(
          collection(db, "ventas"),
          where("fecha", "==", hoy)
        );
        const ventasSnapshot = await getDocs(ventasQuery);
        const ventasData = ventasSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          fecha: doc.data().fecha.toDate()
        }));
        setVentasDelDia(ventasData);
        setTotalDelDia(ventasData.reduce((sum, v) => sum + v.total, 0));
        calcularTopProductos(ventasData);
        calcularEstadisticasMetodoPago(ventasData);
      } catch (error) {
        console.error("Error al cargar ventas:", error);
      }
    };
    cargarVentas();
  }, []);

  // Cargar categorías al inicio
  useEffect(() => {
    cargarCategorias();
  }, []);

  const calcularTopProductos = (ventas) => {
    const productosVendidos = {};
    
    ventas.forEach(venta => {
      venta.productos.forEach(prod => {
        if (productosVendidos[prod.id]) {
          productosVendidos[prod.id].cantidadTotal += prod.cantidadVenta;
          productosVendidos[prod.id].ventasTotal += (prod.precio * prod.cantidadVenta);
        } else {
          productosVendidos[prod.id] = {
            id: prod.id,
            nombre: prod.nombre,
            cantidadTotal: prod.cantidadVenta,
            ventasTotal: prod.precio * prod.cantidadVenta,
            imagenURL: prod.imagenURL
          };
        }
      });
    });

    // Ordenar por cantidadTotal en lugar de ventasTotal
    const topProductosArray = Object.values(productosVendidos)
      .sort((a, b) => b.cantidadTotal - a.cantidadTotal)
      .slice(0, 3);

    setTopProductos(topProductosArray);
  };

  // Función para calcular estadísticas por método de pago
  const calcularEstadisticasMetodoPago = (ventas) => {
    const stats = {
      efectivo: 0,
      tarjeta: 0,
      transferencia: 0
    };
    
    const ventasPorMetodo = {
      efectivo: [],
      tarjeta: [],
      transferencia: []
    };
    
    ventas.forEach(venta => {
      // Si la venta tiene método de pago, sumamos al correspondiente
      const metodoPago = venta.metodoPago ? venta.metodoPago.toLowerCase() : 'efectivo'; // Default a efectivo si no está especificado
      
      if (metodoPago === 'efectivo') {
        stats.efectivo += venta.total;
        ventasPorMetodo.efectivo.push(venta);
      } else if (metodoPago === 'tarjeta') {
        stats.tarjeta += venta.total;
        ventasPorMetodo.tarjeta.push(venta);
      } else if (metodoPago === 'transferencia') {
        stats.transferencia += venta.total;
        ventasPorMetodo.transferencia.push(venta);
      } else {
        // Cualquier otro método (nequi, daviplata, etc.) se considera como efectivo
        stats.efectivo += venta.total;
        ventasPorMetodo.efectivo.push(venta);
      }
    });
    
    setMetodoPagoStats(stats);
    setVentasPorMetodoPago(ventasPorMetodo);
  };

  // Funciones CRUD para categorías
  const cargarCategorias = async () => {
    try {
      setCargandoCategorias(true);
      const categoriasSnapshot = await getDocs(collection(db, "categorias"));
      
      if (categoriasSnapshot.empty) {
        // Si no hay categorías en la base de datos, crearlas a partir del array inicial
        console.log("No hay categorías en la BD, creando categorías iniciales...");
        for (const categoria of CATEGORIAS_PRODUCTOS) {
          await addDoc(collection(db, "categorias"), {
            nombre: categoria,
            fechaCreacion: Timestamp.now()
          });
        }
        setCategorias(CATEGORIAS_PRODUCTOS);
        showToast("Categorías iniciales creadas exitosamente", 'success');
      } else {
        const categoriasData = categoriasSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        const nombresCategorias = categoriasData.map(cat => cat.nombre);
        // Eliminar duplicados usando Set
        const categoriasUnicas = [...new Set(nombresCategorias)];
        
        // Si hay duplicados, limpiar la base de datos
        if (nombresCategorias.length !== categoriasUnicas.length) {
          console.log("Detectados duplicados en categorías, limpiando...");
          
          // Crear un mapa para rastrear qué categorías ya hemos visto
          const categoriasVistas = new Set();
          const duplicadosParaEliminar = [];
          
          categoriasData.forEach(cat => {
            if (categoriasVistas.has(cat.nombre)) {
              // Esta categoría ya existe, marcar para eliminación
              duplicadosParaEliminar.push(cat.id);
            } else {
              categoriasVistas.add(cat.nombre);
            }
          });
          
          // Eliminar duplicados de la base de datos
          for (const idDuplicado of duplicadosParaEliminar) {
            try {
              await deleteDoc(doc(db, "categorias", idDuplicado));
              console.log(`Categoría duplicada eliminada: ${idDuplicado}`);
            } catch (error) {
              console.error(`Error al eliminar categoría duplicada ${idDuplicado}:`, error);
            }
          }
          
          if (duplicadosParaEliminar.length > 0) {
            showToast(`Se eliminaron ${duplicadosParaEliminar.length} categorías duplicadas`, 'success');
          }
        }
        
        setCategorias(categoriasUnicas);
      }
    } catch (error) {
      console.error("Error al cargar categorías:", error);
      showToast("Error al cargar categorías: " + error.message, 'error');
      // En caso de error, usar las categorías por defecto
      setCategorias(CATEGORIAS_PRODUCTOS);
    } finally {
      setCargandoCategorias(false);
    }
  };

  const agregarCategoria = async (e) => {
    e.preventDefault();
    if (!nuevaCategoria.trim()) {
      showToast("El nombre de la categoría es obligatorio", 'error');
      return;
    }

    // Verificar si la categoría ya existe en el estado local
    if (categorias.some(cat => cat.toLowerCase() === nuevaCategoria.toLowerCase())) {
      showToast("Esta categoría ya existe", 'error');
      return;
    }

    try {
      // Verificar también en la base de datos para estar seguros
      const categoriasQuery = query(
        collection(db, "categorias"), 
        where("nombre", "==", nuevaCategoria.trim())
      );
      const categoriaExistente = await getDocs(categoriasQuery);
      
      if (!categoriaExistente.empty) {
        showToast("Esta categoría ya existe en la base de datos", 'error');
        return;
      }

      const docRef = await addDoc(collection(db, "categorias"), {
        nombre: nuevaCategoria.trim(),
        fechaCreacion: Timestamp.now()
      });
      
      setCategorias([...categorias, nuevaCategoria.trim()]);
      setNuevaCategoria('');
      showToast("Categoría agregada exitosamente", 'success');
    } catch (error) {
      console.error("Error al agregar categoría:", error);
      showToast("Error al agregar categoría: " + error.message, 'error');
    }
  };

  const editarCategoria = async (categoriaAntigua, categoriaNueva) => {
    if (!categoriaNueva.trim()) {
      showToast("El nombre de la categoría es obligatorio", 'error');
      return false;
    }

    // Verificar si la nueva categoría ya existe (excepto la actual)
    if (categorias.some(cat => cat.toLowerCase() === categoriaNueva.toLowerCase() && cat !== categoriaAntigua)) {
      showToast("Ya existe una categoría con ese nombre", 'error');
      return false;
    }

    try {
      // Buscar la categoría en la base de datos
      const categoriasQuery = query(
        collection(db, "categorias"), 
        where("nombre", "==", categoriaAntigua)
      );
      const categoriaSnapshot = await getDocs(categoriasQuery);
      
      if (!categoriaSnapshot.empty) {
        const categoriaDoc = categoriaSnapshot.docs[0];
        await updateDoc(doc(db, "categorias", categoriaDoc.id), {
          nombre: categoriaNueva.trim()
        });
        
        // Actualizar también todos los productos que tengan esta categoría
        const productosQuery = query(
          collection(db, "productos"),
          where("categoria", "==", categoriaAntigua)
        );
        const productosSnapshot = await getDocs(productosQuery);
        
        const actualizaciones = productosSnapshot.docs.map(productoDoc => 
          updateDoc(doc(db, "productos", productoDoc.id), {
            categoria: categoriaNueva.trim()
          })
        );
        
        await Promise.all(actualizaciones);
        
        // Actualizar estado local
        const nuevasCategorias = categorias.map(cat => 
          cat === categoriaAntigua ? categoriaNueva.trim() : cat
        );
        setCategorias(nuevasCategorias);
        setEditandoCategoria(null);
        
        showToast(`Categoría actualizada exitosamente. ${productosSnapshot.docs.length} productos actualizados.`, 'success');
        
        // Recargar productos para reflejar los cambios
        cargarProductos();
        return true;
      }
    } catch (error) {
      console.error("Error al editar categoría:", error);
      showToast("Error al editar categoría: " + error.message, 'error');
      return false;
    }
  };

  const eliminarCategoria = async (categoria) => {
    try {
      // Verificar si hay productos usando esta categoría
      const productosQuery = query(
        collection(db, "productos"),
        where("categoria", "==", categoria)
      );
      const productosSnapshot = await getDocs(productosQuery);
      
      if (!productosSnapshot.empty) {
        showToast(`No se puede eliminar la categoría "${categoria}" porque tiene ${productosSnapshot.docs.length} producto(s) asociado(s)`, 'error');
        return false;
      }

      // Buscar y eliminar la categoría de la base de datos
      const categoriasQuery = query(
        collection(db, "categorias"), 
        where("nombre", "==", categoria)
      );
      const categoriaSnapshot = await getDocs(categoriasQuery);
      
      if (!categoriaSnapshot.empty) {
        await deleteDoc(doc(db, "categorias", categoriaSnapshot.docs[0].id));
        
        // Actualizar estado local
        setCategorias(categorias.filter(cat => cat !== categoria));
        showToast("Categoría eliminada exitosamente", 'success');
        return true;
      }
    } catch (error) {
      console.error("Error al eliminar categoría:", error);
      showToast("Error al eliminar categoría: " + error.message, 'error');
      return false;
    }
  };
  
  // Función para comprimir imagen con compresión más agresiva
  const comprimirImagen = (file, maxSizeMB = 0.5) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          // Crear un canvas para comprimir la imagen
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Calcular dimensiones para mantener la relación de aspecto
          // Reducir a dimensiones más pequeñas para mejorar la compresión
          const MAX_WIDTH = 800;
          const MAX_HEIGHT = 800;
          
          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = "#FFFFFF"; // Fondo blanco para imágenes con transparencia
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convertir siempre a JPEG para máxima compresión
          const mimeType = 'image/jpeg';
          const quality = 0.6; // 60% de calidad, mayor compresión
          
          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error('Error al comprimir la imagen'));
              return;
            }
            
            // Si sigue siendo grande, intentar comprimir más
            if (blob.size > maxSizeMB * 1024 * 1024) {
              // Intentar con calidad aún más baja
              canvas.toBlob((blob2) => {
                if (!blob2) {
                  // Si falla la segunda compresión, usar la primera
                  const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".jpg", { 
                    type: mimeType,
                    lastModified: Date.now()
                  });
                  console.log(`Imagen comprimida (nivel 1): ${file.size} -> ${compressedFile.size} bytes`);
                  resolve(compressedFile);
                } else {
                  const compressedFile = new File([blob2], file.name.replace(/\.[^/.]+$/, "") + ".jpg", { 
                    type: mimeType,
                    lastModified: Date.now()
                  });
                  console.log(`Imagen comprimida (nivel 2): ${file.size} -> ${compressedFile.size} bytes`);
                  resolve(compressedFile);
                }
              }, mimeType, 0.4); // 40% calidad, compresión más agresiva
            } else {
              // Crear un nuevo archivo con el mismo nombre base pero extensión jpg
              const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".jpg", { 
                type: mimeType,
                lastModified: Date.now()
              });
              
              console.log(`Imagen comprimida: ${file.size} -> ${compressedFile.size} bytes`);
              resolve(compressedFile);
            }
          }, mimeType, quality);
        };
        img.onerror = (error) => {
          reject(error);
        };
      };
      reader.onerror = (error) => {
        reject(error);
      };
    });
  };

  // Cargar imagenes y manejar cambios de estado
  const manejarImagen = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        // Verificar que sea un formato de imagen válido
        const tiposPermitidos = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
        if (!tiposPermitidos.includes(file.type)) {
          showToast("Formato de imagen no soportado. Use JPG, PNG, GIF o WEBP.", "error");
          return;
        }
        
        // Mostrar mensaje de procesamiento
        showToast("Procesando imagen...", "info");
        
        // Verificar tamaño antes de comprimir
        if (file.size > 5 * 1024 * 1024) { // Si es mayor a 5MB
          showToast("La imagen es muy grande (más de 5MB). Se intentará comprimir, pero puede fallar.", "warning");
        }
        
        // Crear URL temporal para vista previa
        const imageUrl = URL.createObjectURL(file);
        
        // Comprimir TODAS las imágenes, no solo las grandes
        let imagenProcesada;
        try {
          console.log("Iniciando compresión de imagen:", file.name, "Tamaño original:", Math.round(file.size/1024), "KB");
          imagenProcesada = await comprimirImagen(file);
          const reduccion = Math.round(100 - (imagenProcesada.size / file.size * 100));
          console.log("Compresión completada. Tamaño final:", Math.round(imagenProcesada.size/1024), "KB", `(${reduccion}% reducción)`);
          
          // Si la imagen comprimida es más grande que la original (raro pero posible con PNG), usar la original
          if (imagenProcesada.size > file.size) {
            console.log("La imagen comprimida es más grande que la original, usando original");
            imagenProcesada = file;
          }
          
          showToast(`Imagen procesada: ${Math.round(imagenProcesada.size/1024)}KB`, "success");
        } catch (err) {
          console.error("Error al comprimir:", err);
          showToast("Error al comprimir la imagen, usando original", "warning");
          imagenProcesada = file;
        }
        
        setNuevoProducto({ 
          ...nuevoProducto, 
          imagen: imagenProcesada,
          imagenURL: imageUrl 
        });
        
        // Advertir si la imagen sigue siendo grande
        if (imagenProcesada.size > 1 * 1024 * 1024) {
          showToast(`Advertencia: La imagen sigue siendo grande (${Math.round(imagenProcesada.size/1024)}KB). Intente con otra imagen más pequeña.`, "warning");
        }
      } catch (error) {
        console.error("Error al procesar la imagen:", error);
        showToast("Error al procesar la imagen", "error");
      }
    }
  };

  const manejarImagenURL = (e) => {
    // Si el campo está vacío, simplemente limpiar la imagen
    if (!e.target.value) {
      setNuevoProducto({
        ...nuevoProducto,
        imagen: null,
        imagenURL: ''
      });
      return;
    }
    
    // Validar que la URL sea válida
    try {
      new URL(e.target.value);
      
      // Actualizar estado
      setNuevoProducto({
        ...nuevoProducto,
        imagen: null,
        imagenURL: e.target.value
      });
    } catch (error) {
      showToast("URL de imagen no válida", "error");
    }
  };

  const manejarImagenURLEdicion = (e) => {
    // Si el campo está vacío, simplemente limpiar la imagen
    if (!e.target.value) {
      setEditandoProducto({
        ...editandoProducto,
        imagen: null,
        imagenURL: ''
      });
      return;
    }
    
    // Validar que la URL sea válida
    try {
      new URL(e.target.value);
      
      // Actualizar estado
      setEditandoProducto({
        ...editandoProducto,
        imagen: null,
        imagenURL: e.target.value
      });
    } catch (error) {
      showToast("URL de imagen no válida", "error");
    }
  };

  const manejarImagenEdicion = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        // Verificar que sea un formato de imagen válido
        const tiposPermitidos = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
        if (!tiposPermitidos.includes(file.type)) {
          showToast("Formato de imagen no soportado. Use JPG, PNG, GIF o WEBP.", "error");
          return;
        }
        
        // Mostrar mensaje de procesamiento
        showToast("Procesando imagen...", "info");
        
        // Verificar tamaño antes de comprimir
        if (file.size > 5 * 1024 * 1024) { // Si es mayor a 5MB
          showToast("La imagen es muy grande (más de 5MB). Se intentará comprimir, pero puede fallar.", "warning");
        }
        
        // Crear URL temporal para vista previa
        const imageUrl = URL.createObjectURL(file);
        
        // Comprimir la imagen
        let imagenProcesada;
        try {
          console.log("Iniciando compresión de imagen:", file.name, "Tamaño original:", Math.round(file.size/1024), "KB");
          imagenProcesada = await comprimirImagen(file);
          const reduccion = Math.round(100 - (imagenProcesada.size / file.size * 100));
          console.log("Compresión completada. Tamaño final:", Math.round(imagenProcesada.size/1024), "KB", `(${reduccion}% reducción)`);
          
          // Si la imagen comprimida es más grande que la original (raro pero posible con PNG), usar la original
          if (imagenProcesada.size > file.size) {
            console.log("La imagen comprimida es más grande que la original, usando original");
            imagenProcesada = file;
          }
          
          showToast(`Imagen procesada: ${Math.round(imagenProcesada.size/1024)}KB`, "success");
        } catch (err) {
          console.error("Error al comprimir:", err);
          showToast("Error al comprimir la imagen, usando original", "warning");
          imagenProcesada = file;
        }
        
        setEditandoProducto({
          ...editandoProducto,
          imagen: imagenProcesada,
          imagenURL: imageUrl
        });
        
        // Advertir si la imagen sigue siendo grande
        if (imagenProcesada.size > 1 * 1024 * 1024) {
          showToast(`Advertencia: La imagen sigue siendo grande (${Math.round(imagenProcesada.size/1024)}KB). Intente con otra imagen más pequeña.`, "warning");
        }
      } catch (error) {
        console.error("Error al procesar la imagen:", error);
        showToast("Error al procesar la imagen", "error");
      }
    }
  };

  // Eliminar productos y vendedores
  const eliminarProducto = async (producto) => {
    try {
      // Si el producto tiene ID de Firebase, eliminarlo de Firebase
      if (producto.id) {
        await deleteDoc(doc(db, "productos", producto.id));
      }
      setProductos(productos.filter(p => p.id !== producto.id));
      showToast('Producto eliminado correctamente');
    } catch (error) {
      console.error("Error al eliminar producto:", error);
      showToast(`Error al eliminar producto: ${error.message}`, 'error');
    }
  };

  const agregarVendedor = async (e) => {
    e.preventDefault();
    console.log("🚀 Iniciando agregarVendedor...", nuevoVendedor);
    
    // Validación básica
    if (!nuevoVendedor.username || nuevoVendedor.username.trim() === '') {
      console.log("❌ Username vacío o inválido");
      showToast("Por favor ingrese un nombre de usuario válido", 'error');
      return;
    }

    if (!nuevoVendedor.password || nuevoVendedor.password.trim() === '') {
      console.log("❌ Password vacío o inválido");
      showToast("Por favor ingrese una contraseña válida", 'error');
      return;
    }

    try {
      console.log("✅ Validaciones básicas pasadas");
      
      // Check if username already exists
      console.log("🔍 Verificando si username existe...");
      const usernameQuery = query(
        collection(db, "vendedores"), 
        where("username", "==", nuevoVendedor.username)
      );
      const usernameSnapshot = await getDocs(usernameQuery);

      if (!usernameSnapshot.empty) {
        console.log("❌ Username ya existe");
        showToast("Ya existe un usuario con ese nombre. Por favor elija otro nombre.", 'error');
        return;
      }

      console.log("✅ Username disponible");
      showToast("Creando vendedor...", 'info');

      // Preparar imagen - usar Base64 si hay imagen, sino imagen por defecto
      let imagenURL = DEFAULT_PROFILE_IMAGE;
      
      if (nuevoVendedor.imagen && nuevoVendedor.imagenURL !== DEFAULT_PROFILE_IMAGE) {
        console.log("📸 Procesando imagen personalizada...");
        try {
          // Comprimir la imagen para mejor rendimiento
          imagenURL = await comprimirImagenBase64(nuevoVendedor.imagenURL, 150, 150, 0.7);
          console.log("✅ Imagen comprimida y lista:", imagenURL.substring(0, 50) + "...");
          showToast("Imagen procesada correctamente", 'success');
        } catch (error) {
          console.error("❌ Error al procesar imagen:", error);
          showToast("Error al procesar imagen, se usará imagen por defecto", 'warning');
          imagenURL = DEFAULT_PROFILE_IMAGE;
        }
      } else {
        console.log("📸 Usando imagen por defecto");
      }

      const vendedorData = {
        username: nuevoVendedor.username,
        password: nuevoVendedor.password,
        rol: nuevoVendedor.rol || 'vendedor',
        imagenURL: imagenURL,
        sucursal: nuevoVendedor.sucursal || SUCURSALES_DISPONIBLES[0],
        fechaCreacion: new Date()
      };

      console.log("💾 Guardando vendedor en Firestore:", vendedorData);
      
      // Guardar en Firestore
      const docRef = await addDoc(collection(db, "vendedores"), vendedorData);
      console.log("✅ Vendedor guardado con ID:", docRef.id);

      // Actualizar estado local
      const nuevoVendedorConId = { 
        id: docRef.id, 
        ...vendedorData 
      };
      setVendedores(prevVendedores => [...prevVendedores, nuevoVendedorConId]);
      console.log("✅ Estado local actualizado con vendedor:", nuevoVendedorConId);

      // Limpiar formulario
      setNuevoVendedor({
        username: '',
        password: '',
        rol: 'vendedor',
        imagen: null,
        imagenURL: DEFAULT_PROFILE_IMAGE,
        sucursal: SUCURSALES_DISPONIBLES[0]
      });
      console.log("✅ Formulario limpiado");

      showToast("Usuario agregado exitosamente!", 'success');
      console.log("🎉 Proceso completado exitosamente");
      
    } catch (error) {
      console.error("❌ Error al agregar usuario:", error);
      console.error("❌ Detalles del error:", {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      showToast("Error al agregar usuario: " + error.message, 'error');
    }
  };

  const eliminarVendedor = async (vendedor) => {
    try {
      // Eliminar de Firebase
      await deleteDoc(doc(db, "vendedores", vendedor.id));
      
      // Actualizar estado local
      setVendedores(vendedores.filter(v => v.id !== vendedor.id));
      showToast('Usuario eliminado correctamente');
    } catch (error) {
      console.error("Error al eliminar usuario:", error);
      showToast(`Error al eliminar usuario: ${error.message}`, 'error');
    }
  };

  const iniciarEdicionProducto = (producto, index) => {
    setEditandoProducto({ ...producto, index });
  };

  const guardarEdicionProducto = async () => {
    try {
      // Validaciones básicas
      if (!editandoProducto.nombre || editandoProducto.nombre.trim() === '') {
        showToast("El nombre del producto es obligatorio", 'error');
        return;
      }
      
      if (!editandoProducto.precio || isNaN(parseFloat(editandoProducto.precio))) {
        showToast("El precio debe ser un número válido", 'error');
        return;
      }
      
      showToast("Actualizando producto...", 'info');
      
      let imagenURL = editandoProducto.imagenURL;

      // Si la imagen es un blob temporal, necesita ser procesada
      if (imagenURL && imagenURL.startsWith('blob:')) {
        if (!editandoProducto.imagen) {
          showToast("Error con la imagen seleccionada. Por favor, selecciónela nuevamente.", 'error');
          return;
        }
      }

      // Si hay una nueva imagen archivo, subirla
      if (editandoProducto.imagen && typeof editandoProducto.imagen === 'object') {
        try {
          // Verificar el tamaño una vez más antes de subir
          if (editandoProducto.imagen.size > 2 * 1024 * 1024) {
            showToast(`Imagen demasiado grande: ${Math.round(editandoProducto.imagen.size/1024)}KB. Intentando comprimir más...`, "warning");
            
            try {
              // Intentar comprimir aún más
              const imagenMasComprimida = await comprimirImagen(editandoProducto.imagen, 0.3);
              if (imagenMasComprimida.size < editandoProducto.imagen.size) {
                showToast(`Compresión adicional aplicada: ${Math.round(imagenMasComprimida.size/1024)}KB`, "info");
                editandoProducto.imagen = imagenMasComprimida;
              }
            } catch (compressError) {
              console.error("Error en compresión adicional:", compressError);
            }
          }
          
          showToast("Subiendo imagen... Por favor, espere.", 'info');
          
          // Generar nombre único para la imagen
          const timestamp = new Date().getTime();
          const nombreLimpio = editandoProducto.imagen.name.replace(/[^a-zA-Z0-9.]/g, '_');
          const nombreArchivo = `${timestamp}_${nombreLimpio}`;
          
          const imageRef = ref(storage, `productos/${nombreArchivo}`);
          
          // Subir la imagen con un tiempo límite
          const uploadPromise = uploadBytes(imageRef, editandoProducto.imagen);
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error("Tiempo de espera agotado durante la subida")), 60000) // 60 segundos
          );
          
          console.log(`Iniciando subida de imagen (${Math.round(editandoProducto.imagen.size/1024)}KB)...`);
          const uploadResult = await Promise.race([uploadPromise, timeoutPromise]);
          console.log("Imagen subida con éxito:", uploadResult);
          
          // Obtener URL de descarga
          imagenURL = await getDownloadURL(imageRef);
          console.log("URL de imagen obtenida:", imagenURL);
          
        } catch (uploadError) {
          console.error("Error detallado al subir la imagen:", uploadError);
          
          // Intentar solución alternativa con base64 para imágenes pequeñas
          if (editandoProducto.imagen.size <= 100 * 1024) { // Menos de 100KB
            showToast("Intentando método alternativo para guardar la imagen...", 'info');
            
            try {
              const reader = new FileReader();
              reader.readAsDataURL(editandoProducto.imagen);
              
              await new Promise((resolve, reject) => {
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
              });
              
              imagenURL = reader.result;
            } catch (altError) {
              console.error("Error en método alternativo:", altError);
              showToast("No se pudo guardar la imagen. Intente con una URL externa.", 'error');
              return;
            }
          } else {
            showToast(`Error al subir la imagen: ${uploadError.message}. Intente con una imagen más pequeña o use una URL.`, 'error');
            return;
          }
        }
      }
      
      // Si la URL es un blob pero no hay imagen procesada, mostrar error
      if (imagenURL && imagenURL.startsWith('blob:') && !imagenURL.startsWith('data:')) {
        showToast("La URL de la imagen no es válida. Por favor, seleccione la imagen nuevamente o use una URL externa.", 'error');
        return;
      }

      const productoActualizado = {
        nombre: editandoProducto.nombre,
        cantidad: editandoProducto.cantidad ? parseInt(editandoProducto.cantidad) : null,
        precio: parseFloat(editandoProducto.precio),
        imagenURL: imagenURL || '',
        categoria: editandoProducto.categoria || categorias[0] || 'Postres', // Asegurar que tenga categoría
        sucursalOrigen: editandoProducto.sucursalOrigen || NOMBRES_SUCURSALES[0], // Asegurar que tenga sucursal origen
        ultimaActualizacion: new Date()
      };
      
      // Si la imagen es base64, marcarla como tal
      if (imagenURL && imagenURL.startsWith('data:')) {
        productoActualizado.imagenEsBase64 = true;
      }

      // Actualizar en Firebase
      await updateDoc(doc(db, "productos", editandoProducto.id), productoActualizado);

      // Actualizar estado local
      const nuevosProductos = productos.map(p => 
        p.id === editandoProducto.id ? {...productoActualizado, id: editandoProducto.id} : p
      );
      setProductos(nuevosProductos);
      setEditandoProducto(null);
      showToast("¡Producto actualizado exitosamente!", 'success');
    } catch (error) {
      console.error("Error detallado al editar producto:", error);
      
      // Mostrar un mensaje de error más descriptivo según el tipo de error
      if (error.code === 'storage/unauthorized') {
        showToast("Error de permisos: No tienes autorización para subir imágenes.", 'error');
      } else if (error.code === 'storage/canceled') {
        showToast("La subida de la imagen fue cancelada.", 'error');
      } else if (error.code === 'storage/retry-limit-exceeded') {
        showToast("Error de red: Comprueba tu conexión a internet e intenta de nuevo.", 'error');
      } else if (error.code && error.code.startsWith('storage/')) {
        showToast(`Error de almacenamiento: ${error.message}`, 'error');
      } else {
        showToast(`Error al editar producto: ${error.message}`, 'error');
      }
    }
  };

  const iniciarEdicionVendedor = (vendedor, index) => {
    // Asegurar que tenga una imagenURL y sucursal, o usar las opciones predeterminadas
    const vendedorConImagen = {
      ...vendedor,
      imagenURL: vendedor.imagenURL || DEFAULT_PROFILE_IMAGE,
      imagen: null, // Para nueva imagen si se selecciona
      sucursal: vendedor.sucursal || SUCURSALES_DISPONIBLES[0],
      index
    };
    setEditandoVendedor(vendedorConImagen);
  };

  // Modificar guardarEdicionVendedor para actualizar el perfil del usuario actual cuando se edita su información
  const guardarEdicionVendedor = async () => {
    try {
      showToast("Actualizando vendedor...", 'info');

      // Preparar imagen - procesar si hay nueva imagen
      let imagenURL = editandoVendedor.imagenURL || DEFAULT_PROFILE_IMAGE;
      
      if (editandoVendedor.imagen && editandoVendedor.imagenURL !== DEFAULT_PROFILE_IMAGE) {
        console.log("📸 Procesando nueva imagen...");
        try {
          // Comprimir la nueva imagen
          imagenURL = await comprimirImagenBase64(editandoVendedor.imagenURL, 150, 150, 0.7);
          console.log("✅ Nueva imagen procesada");
        } catch (error) {
          console.error("❌ Error al procesar nueva imagen:", error);
          showToast("Error al procesar nueva imagen, se mantendrá la anterior", 'warning');
          // Mantener la imagen anterior
          imagenURL = editandoVendedor.imagenURL;
        }
      }

      const vendedorActualizado = {
        username: editandoVendedor.username,
        password: editandoVendedor.password,
        rol: editandoVendedor.rol,
        imagenURL: imagenURL,
        sucursal: editandoVendedor.sucursal
      };

      // Actualizar en Firebase
      await updateDoc(doc(db, "vendedores", editandoVendedor.id), vendedorActualizado);

      // Actualizar estado local
      const nuevosVendedores = vendedores.map(v => 
        v.id === editandoVendedor.id ? {...vendedorActualizado, id: editandoVendedor.id} : v
      );
      setVendedores(nuevosVendedores);

      // Si el usuario editado es el usuario actual, actualizar su perfil
      const userData = JSON.parse(localStorage.getItem('user'));
      if (userData && userData.username === editandoVendedor.username) {
        setUserProfile({
          ...userProfile,
          imagenURL: imagenURL
        });
        
        // Actualizar también en localStorage para mantener consistencia
        const updatedUserData = {
          ...userData,
          imagenURL: imagenURL
        };
        localStorage.setItem('user', JSON.stringify(updatedUserData));
      }

      setEditandoVendedor(null);
      showToast("Usuario actualizado exitosamente!", 'success');
    } catch (error) {
      console.error("Error al editar usuario:", error);
      showToast("Error al editar usuario: " + error.message, 'error');
    }
  };

  // Añadir función para refrescar el perfil del usuario actual
  const refrescarPerfilUsuario = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      if (userData && userData.username) {
        const vendedorActual = vendedores.find(v => v.username === userData.username);
        if (vendedorActual) {
          setUserProfile({
            nombre: vendedorActual.username,
            rol: vendedorActual.rol,
            imagenURL: vendedorActual.imagenURL
          });
        }
      }
    } catch (error) {
      console.error("Error al refrescar perfil:", error);
    }
  };

  // Añadir efecto para refrescar el perfil cuando cambien los vendedores
  useEffect(() => {
    refrescarPerfilUsuario();
  }, [vendedores]);

  const handleLogout = () => {
    // Add any cleanup logic here (clear tokens, etc)
    localStorage.removeItem('user'); // If you're storing user data
    sessionStorage.clear(); // Clear any session data
    navigate('/'); // Navigate to login page
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'decimal',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    }).format(number);
  };

  // Funciones para gestión de mesas
  const cargarMesas = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "mesas"));
      const mesasDB = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Agrupar mesas por sucursal
      const mesasAgrupadas = {};
      SUCURSALES_DISPONIBLES.forEach(sucursal => {
        mesasAgrupadas[sucursal] = mesasDB.filter(mesa => mesa.sucursal === sucursal);
      });
      
      setMesasPorSucursal(mesasAgrupadas);
    } catch (error) {
      console.error("Error al cargar mesas:", error);
      showToast("Error al cargar mesas", 'error');
    }
  };

  const agregarMesa = async (e) => {
    e.preventDefault();
    try {
      // Verificar si ya existe una mesa con ese número en la sucursal
      const mesasExistentes = mesasPorSucursal[nuevaMesa.sucursal] || [];
      const mesaExiste = mesasExistentes.some(mesa => mesa.numero === nuevaMesa.numero);
      
      if (mesaExiste) {
        showToast(`Ya existe la mesa ${nuevaMesa.numero} en ${nuevaMesa.sucursal}`, 'error');
        return;
      }

      const mesaData = {
        numero: nuevaMesa.numero,
        sucursal: nuevaMesa.sucursal,
        capacidad: parseInt(nuevaMesa.capacidad),
        estado: nuevaMesa.estado,
        fechaCreacion: new Date()
      };

      const docRef = await addDoc(collection(db, "mesas"), mesaData);
      
      // Actualizar estado local
      setMesasPorSucursal(prev => ({
        ...prev,
        [nuevaMesa.sucursal]: [...(prev[nuevaMesa.sucursal] || []), { id: docRef.id, ...mesaData }]
      }));

      // Limpiar formulario
      setNuevaMesa({
        sucursal: SUCURSALES_DISPONIBLES[0],
        numero: '',
        capacidad: '',
        estado: 'disponible'
      });

      showToast("Mesa agregada exitosamente!");
    } catch (error) {
      console.error("Error al agregar mesa:", error);
      showToast("Error al agregar mesa: " + error.message, 'error');
    }
  };

  const eliminarMesa = async (mesa) => {
    try {
      await deleteDoc(doc(db, "mesas", mesa.id));
      
      // Actualizar estado local
      setMesasPorSucursal(prev => ({
        ...prev,
        [mesa.sucursal]: prev[mesa.sucursal].filter(m => m.id !== mesa.id)
      }));
      
      showToast('Mesa eliminada correctamente');
    } catch (error) {
      console.error("Error al eliminar mesa:", error);
      showToast(`Error al eliminar mesa: ${error.message}`, 'error');
    }
  };

  const iniciarEdicionMesa = (mesa) => {
    setEditandoMesa({ ...mesa });
  };

  const guardarEdicionMesa = async () => {
    try {
      // Verificar si el número de mesa no está duplicado (excluyendo la mesa actual)
      const mesasExistentes = mesasPorSucursal[editandoMesa.sucursal] || [];
      const mesaExiste = mesasExistentes.some(mesa => 
        mesa.numero === editandoMesa.numero && mesa.id !== editandoMesa.id
      );
      
      if (mesaExiste) {
        showToast(`Ya existe la mesa ${editandoMesa.numero} en ${editandoMesa.sucursal}`, 'error');
        return;
      }

      const mesaActualizada = {
        numero: editandoMesa.numero,
        sucursal: editandoMesa.sucursal,
        capacidad: parseInt(editandoMesa.capacidad),
        estado: editandoMesa.estado
      };

      await updateDoc(doc(db, "mesas", editandoMesa.id), mesaActualizada);

      // Actualizar estado local
      setMesasPorSucursal(prev => {
        const nuevasMesas = { ...prev };
        
        // Remover de la sucursal anterior si cambió
        Object.keys(nuevasMesas).forEach(sucursal => {
          nuevasMesas[sucursal] = nuevasMesas[sucursal].filter(m => m.id !== editandoMesa.id);
        });
        
        // Agregar a la nueva sucursal
        if (!nuevasMesas[editandoMesa.sucursal]) {
          nuevasMesas[editandoMesa.sucursal] = [];
        }
        nuevasMesas[editandoMesa.sucursal].push({ id: editandoMesa.id, ...mesaActualizada });
        
        return nuevasMesas;
      });

      setEditandoMesa(null);
      showToast("Mesa actualizada exitosamente!");
    } catch (error) {
      console.error("Error al editar mesa:", error);
      showToast("Error al editar mesa: " + error.message, 'error');
    }
  };

  // Cargar mesas al inicio
  useEffect(() => {
    cargarMesas();
  }, []);

  // Funciones para gestión de ventas interlócales
  const cargarVentasInterlocales = async () => {
    try {
      console.log("🔄 Cargando ventas interlócales para el rango:", dateRange);
      
      const ventasQuery = query(
        collection(db, "ventas_interlocales"),
        where("fecha", ">=", new Date(dateRange.start + 'T00:00:00')),
        where("fecha", "<=", new Date(dateRange.end + 'T23:59:59'))
      );
      
      const ventasSnapshot = await getDocs(ventasQuery);
      const ventasData = ventasSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        fecha: doc.data().fecha.toDate()
      }));

      console.log("📊 Ventas interlócales encontradas:", ventasData.length, ventasData);
      
      setVentasInterlocales(ventasData);
      calcularComisionesPendientes(ventasData);
      
      showToast(`Se cargaron ${ventasData.length} transacciones de comisiones`, 'success');
    } catch (error) {
      console.error("❌ Error al cargar ventas interlócales:", error);
      showToast("Error al cargar ventas interlócales: " + error.message, 'error');
    }
  };

  const calcularComisionesPendientes = (ventas) => {
    console.log("🧮 Calculando comisiones pendientes para:", ventas.length, "ventas");
    
    const comisiones = {};
    
    ventas.forEach((venta, index) => {
      console.log(`💰 Procesando venta ${index + 1}:`, {
        id: venta.id,
        estado: venta.estado,
        sucursalVendedora: venta.sucursalVendedora,
        sucursalAcreedora: venta.sucursalAcreedora,
        totalComision: venta.totalComision
      });
      
      if (venta.estado === 'pendiente') {
        const sucursalVendedora = venta.sucursalVendedora;
        const sucursalAcreedora = venta.sucursalAcreedora;
        const comision = venta.totalComision || 0;
        
        if (!comisiones[sucursalVendedora]) {
          comisiones[sucursalVendedora] = {
            debe: 0,
            recibe: 0,
            detalle: []
          };
        }
        
        if (!comisiones[sucursalAcreedora]) {
          comisiones[sucursalAcreedora] = {
            debe: 0,
            recibe: 0,
            detalle: []
          };
        }
        
        comisiones[sucursalVendedora].debe += comision;
        comisiones[sucursalAcreedora].recibe += comision;
        
        comisiones[sucursalVendedora].detalle.push({
          tipo: 'debe',
          monto: comision,
          producto: venta.productos?.[0]?.productoNombre || 'Producto desconocido',
          fecha: venta.fecha
        });
        
        comisiones[sucursalAcreedora].detalle.push({
          tipo: 'recibe',
          monto: comision,
          producto: venta.productos?.[0]?.productoNombre || 'Producto desconocido',
          fecha: venta.fecha
        });
      }
    });
    
    console.log("📊 Comisiones calculadas:", comisiones);
    setReporteComisiones(comisiones);
  };

  const marcarComisionPagada = async (ventaId) => {
    try {
      await updateDoc(doc(db, "ventas_interlocales", ventaId), {
        estado: 'pagada',
        fechaPago: Timestamp.now()
      });
      
      await cargarVentasInterlocales();
      showToast('Comisión marcada como pagada', 'success');
    } catch (error) {
      console.error("Error al marcar comisión:", error);
      showToast('Error al marcar comisión', 'error');
    }
  };

  // Cargar ventas interlócales cuando cambie el rango de fechas
  useEffect(() => {
    cargarVentasInterlocales();
  }, [dateRange]);

  // Cargar ventas por fecha y sucursal
  const cargarVentasPorFecha = async (startDate, endDate) => {
    try {
      const fechaInicio = new Date(startDate + 'T00:00:00');
      const fechaFin = new Date(endDate + 'T23:59:59');

      const ventasQuery = query(
        collection(db, "ventas"),
        where("fecha", ">=", fechaInicio),
        where("fecha", "<=", fechaFin)
      );
      
      const ventasSnapshot = await getDocs(ventasQuery);
      let ventasData = ventasSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        fecha: doc.data().fecha.toDate()
      }));

      // Filtrar por sucursal si no es "Todas"
      if (filtroSucursal !== 'Todas') {
        ventasData = ventasData.filter(venta => venta.sucursal === filtroSucursal);
      }

      setVentasDelDia(ventasData);
      setTotalDelDia(ventasData.reduce((sum, v) => sum + v.total, 0));
      calcularTopProductos(ventasData);
      calcularEstadisticasMetodoPago(ventasData);
    } catch (error) {
      console.error("Error al cargar ventas:", error);
    }
  };

  // Función para agrupar ventas por día
  const agruparVentasPorDia = (ventas) => {
    const ventasPorDia = {};
    
    ventas.forEach(venta => {
      // Usar fecha local directamente sin conversión ISO para evitar problemas de zona horaria
      const fechaFormateada = venta.fecha.toLocaleDateString('es-ES', { 
        day: '2-digit', 
        month: '2-digit',
        timeZone: 'America/Bogota' // Especificar zona horaria de Colombia
      });
      
      if (!ventasPorDia[fechaFormateada]) {
        ventasPorDia[fechaFormateada] = {
          fecha: fechaFormateada,
          total: 0,
          cantidad: 0
        };
      }
      
      ventasPorDia[fechaFormateada].total += venta.total;
      ventasPorDia[fechaFormateada].cantidad += 1;
    });
    
    return Object.values(ventasPorDia).sort((a, b) => {
      const [dayA, monthA] = a.fecha.split('/');
      const [dayB, monthB] = b.fecha.split('/');
      // Usar el año actual 
      const currentYear = new Date().getFullYear();
      return new Date(`${currentYear}-${monthA}-${dayA}`) - new Date(`${currentYear}-${monthB}-${dayB}`);
    });
  };

  // Add useEffect to watch dateRange and filtroSucursal changes
  useEffect(() => {
    cargarVentasPorFecha(dateRange.start, dateRange.end);
  }, [dateRange, filtroSucursal]);

  // Recargar comisiones cuando cambie el rango de fechas
  useEffect(() => {
    console.log("📅 EFFECT: Rango de fechas cambió:", dateRange);
    cargarVentasInterlocales(dateRange);
  }, [dateRange]);

  // Modificar el renderContenido
  const renderContenido = () => {
    switch(seccionActiva) {
      case 'inventario':
        // Productos filtrados por categoría
        const productosFiltrados = filtroCategoria === 'Todas' 
          ? productos 
          : productos.filter(p => p.categoria === filtroCategoria);
          
        return (
          <section style={{ padding: window.innerWidth < 768 ? '10px' : '20px' }}>
            <h3 style={{ 
              fontSize: window.innerWidth < 768 ? '20px' : '24px',
              marginBottom: '20px',
              color: '#333',
              borderBottom: '2px solid #dee2e6',
              paddingBottom: '10px'
            }}>
              📦 Gestión de Inventario
            </h3>
            
            {/* Formulario de agregar producto con mejor diseño móvil */}
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: window.innerWidth < 768 ? '15px' : '20px',
              borderRadius: '12px',
              marginBottom: '25px',
              border: '1px solid #dee2e6',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h4 style={{ 
                margin: '0 0 15px 0', 
                color: '#007bff',
                fontSize: window.innerWidth < 768 ? '16px' : '18px'
              }}>
                ➕ Agregar Nuevo Producto
              </h4>
              
              <form onSubmit={agregarProducto}>
                <input
                  type="text"
                  placeholder="Nombre del producto"
                  value={nuevoProducto.nombre}
                  onChange={(e) => setNuevoProducto({ ...nuevoProducto, nombre: e.target.value })}
                  required
                  style={{
                    padding: getResponsiveSize('12px', '15px'),
                    fontSize: getResponsiveSize('14px', '16px'),
                    marginBottom: '15px',
                    border: '1px solid #ced4da',
                    borderRadius: '8px',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                />
                
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr',
                  gap: '15px', 
                  marginBottom: '15px' 
                }}>
                  <input
                    type="number"
                    placeholder="Cantidad (opcional)"
                    value={nuevoProducto.cantidad}
                    onChange={(e) => setNuevoProducto({ ...nuevoProducto, cantidad: e.target.value })}
                    style={{
                      padding: getResponsiveSize('12px', '15px'),
                      fontSize: getResponsiveSize('14px', '16px'),
                      border: '1px solid #ced4da',
                      borderRadius: '8px',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  />
                  <input
                    type="number"
                    placeholder="Precio"
                    value={nuevoProducto.precio}
                    onChange={(e) => setNuevoProducto({ ...nuevoProducto, precio: e.target.value })}
                    required
                    style={{
                      padding: getResponsiveSize('12px', '15px'),
                      fontSize: getResponsiveSize('14px', '16px'),
                      border: '1px solid #ced4da',
                      borderRadius: '8px',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                
                {/* Selector de categoría */}
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    fontWeight: 'bold',
                    color: '#495057',
                    fontSize: getResponsiveSize('14px', '16px')
                  }}>
                    Categoría:
                  </label>
                  <select
                    value={nuevoProducto.categoria}
                    onChange={(e) => setNuevoProducto({ ...nuevoProducto, categoria: e.target.value })}
                    required
                    style={{
                      padding: getResponsiveSize('12px', '15px'),
                      fontSize: getResponsiveSize('14px', '16px'),
                      border: '1px solid #ced4da',
                      borderRadius: '8px',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  >
                    {categorias.map((cat, index) => (
                      <option key={index} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                
                {/* Selector de sucursal origen */}
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    fontWeight: 'bold',
                    color: '#495057',
                    fontSize: getResponsiveSize('14px', '16px')
                  }}>
                    Sucursal de origen:
                  </label>
                  <select
                    value={nuevoProducto.sucursalOrigen}
                    onChange={(e) => setNuevoProducto({ ...nuevoProducto, sucursalOrigen: e.target.value })}
                    required
                    style={{
                      padding: getResponsiveSize('12px', '15px'),
                      fontSize: getResponsiveSize('14px', '16px'),
                      border: '1px solid #ced4da',
                      borderRadius: '8px',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  >
                    {NOMBRES_SUCURSALES.map((sucursal, index) => (
                      <option key={index} value={sucursal}>{sucursal}</option>
                    ))}
                  </select>
                </div>
                
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    fontWeight: 'bold',
                    color: '#495057',
                    fontSize: getResponsiveSize('14px', '16px')
                  }}>
                    Imagen del producto:
                  </label>
                  
                  {/* Vista previa de la imagen */}
                  {nuevoProducto.imagenURL && (
                    <div style={{ 
                      marginBottom: '15px', 
                      textAlign: 'center',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      padding: '10px'
                    }}>
                      <img 
                        src={nuevoProducto.imagenURL} 
                        alt="Vista previa" 
                        style={{ 
                          maxHeight: '200px', 
                          maxWidth: '100%',
                          borderRadius: '4px'
                        }}
                      />
                    </div>
                  )}
                  
                  <input
                    type="text"
                    placeholder="URL de la imagen (opcional)"
                    value={nuevoProducto.imagenURL && !nuevoProducto.imagen ? nuevoProducto.imagenURL : ''}
                    onChange={manejarImagenURL}
                    style={{ 
                      marginBottom: '10px', 
                      width: '100%',
                      padding: getResponsiveSize('12px', '15px'),
                      fontSize: getResponsiveSize('14px', '16px'),
                      border: '1px solid #ced4da',
                      borderRadius: '8px',
                      boxSizing: 'border-box'
                    }}
                  />
                  <div style={{ 
                    textAlign: 'center', 
                    margin: '10px 0',
                    color: '#6c757d',
                    fontSize: getResponsiveSize('12px', '14px')
                  }}>
                    O
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={manejarImagen}
                    style={{ 
                      padding: getResponsiveSize('12px', '15px'),
                      fontSize: getResponsiveSize('14px', '16px'),
                      border: '1px solid #ced4da',
                      borderRadius: '8px',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  />
                  {nuevoProducto.imagen && (
                    <div style={{ 
                      marginTop: '8px', 
                      fontSize: getResponsiveSize('12px', '14px'),
                      color: '#28a745'
                    }}>
                      ✓ Archivo seleccionado: {nuevoProducto.imagen.name}
                    </div>
                  )}
                </div>
                
                <button 
                  type="submit"
                  style={{
                    padding: getResponsiveSize('15px', '18px'),
                    fontSize: getResponsiveSize('16px', '18px'),
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    width: '100%',
                    fontWeight: 'bold',
                    transition: 'background-color 0.3s ease',
                    boxShadow: '0 2px 4px rgba(40, 167, 69, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#218838'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
                >
                  ✅ Agregar Producto
                </button>
                {/* Aviso sobre imágenes */}
                {nuevoProducto.imagen && (
                  <div style={{
                    fontSize: getResponsiveSize('12px', '14px'),
                    color: '#0066cc',
                    marginTop: '10px',
                    textAlign: 'center'
                  }}>
                    La imagen se subirá automáticamente al guardar el producto
                  </div>
                )}
              </form>
            </div>
            
            {/* Filtro de categorías mejorado */}
            <div style={{ 
              margin: '25px 0', 
              backgroundColor: 'white',
              padding: window.innerWidth < 768 ? '15px' : '20px',
              borderRadius: '12px',
              border: '1px solid #dee2e6',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <label style={{ 
                marginBottom: '12px', 
                fontWeight: 'bold',
                color: '#495057',
                fontSize: getResponsiveSize('16px', '18px'),
                display: 'block'
              }}>
                🔍 Filtrar por categoría:
              </label>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: window.innerWidth < 768 
                  ? 'repeat(auto-fit, minmax(120px, 1fr))'
                  : 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '10px'
              }}>
                <button
                  onClick={() => setFiltroCategoria('Todas')}
                  style={{
                    padding: window.innerWidth < 768 ? '10px 8px' : '12px 15px',
                    backgroundColor: filtroCategoria === 'Todas' ? '#007bff' : '#f8f9fa',
                    color: filtroCategoria === 'Todas' ? 'white' : '#212529',
                    border: filtroCategoria === 'Todas' ? '2px solid #007bff' : '2px solid #dee2e6',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: getResponsiveSize('13px', '14px'),
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  Todas
                </button>
                {[...new Set(categorias)].map((cat, index) => (
                  <button
                    key={index}
                    onClick={() => setFiltroCategoria(cat)}
                    style={{
                      padding: window.innerWidth < 768 ? '10px 8px' : '12px 15px',
                      backgroundColor: filtroCategoria === cat ? '#007bff' : '#f8f9fa',
                      color: filtroCategoria === cat ? 'white' : '#212529',
                      border: filtroCategoria === cat ? '2px solid #007bff' : '2px solid #dee2e6',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: getResponsiveSize('13px', '14px'),
                      fontWeight: '500',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Cuadrícula de productos mejorada */}
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: window.innerWidth < 768 
                ? 'repeat(auto-fill, minmax(160px, 1fr))'
                : 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: getResponsiveSize('12px', '20px'),
              padding: window.innerWidth < 768 ? '10px 0' : '20px 0'
            }}>
              {productosFiltrados.map((p, i) => (
                <div key={p.id || i} style={{ 
                  border: '1px solid #dee2e6',
                  borderRadius: '12px',
                  padding: getResponsiveSize('12px', '15px'),
                  backgroundColor: '#fff',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  cursor: 'pointer'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.15)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                }}>
                  {editandoProducto?.id === p.id ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <input
                        type="text"
                        value={editandoProducto.nombre}
                        onChange={(e) => setEditandoProducto({ ...editandoProducto, nombre: e.target.value })}
                        style={{
                          padding: getResponsiveSize('10px', '12px'),
                          fontSize: getResponsiveSize('14px', '16px'),
                          border: '1px solid #ced4da',
                          borderRadius: '8px',
                          width: '100%',
                          boxSizing: 'border-box'
                        }}
                      />
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr',
                        gap: '10px' 
                      }}>
                        <input
                          type="number"
                          value={editandoProducto.cantidad}
                          onChange={(e) => setEditandoProducto({ ...editandoProducto, cantidad: e.target.value })}
                          placeholder="Cantidad (opcional)"
                          style={{
                            padding: getResponsiveSize('10px', '12px'),
                            fontSize: getResponsiveSize('14px', '16px'),
                            border: '1px solid #ced4da',
                            borderRadius: '8px',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        />
                        <input
                          type="number"
                          value={editandoProducto.precio}
                          onChange={(e) => setEditandoProducto({ ...editandoProducto, precio: e.target.value })}
                          placeholder="Precio"
                          style={{
                            padding: getResponsiveSize('10px', '12px'),
                            fontSize: getResponsiveSize('14px', '16px'),
                            border: '1px solid #ced4da',
                            borderRadius: '8px',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                      
                      {/* Selector de categoría en edición */}
                      <select
                        value={editandoProducto.categoria || categorias[0] || 'Postres'}
                        onChange={(e) => setEditandoProducto({ ...editandoProducto, categoria: e.target.value })}
                        required
                        style={{
                          padding: getResponsiveSize('10px', '12px'),
                          fontSize: getResponsiveSize('14px', '16px'),
                          border: '1px solid #ced4da',
                          borderRadius: '8px',
                          width: '100%',
                          boxSizing: 'border-box'
                        }}
                      >
                        {categorias.map((cat, index) => (
                          <option key={index} value={cat}>{cat}</option>
                        ))}
                      </select>
                      
                      {/* Selector de sucursal origen en edición */}
                      <select
                        value={editandoProducto.sucursalOrigen || NOMBRES_SUCURSALES[0]}
                        onChange={(e) => setEditandoProducto({ ...editandoProducto, sucursalOrigen: e.target.value })}
                        required
                        style={{
                          padding: getResponsiveSize('10px', '12px'),
                          fontSize: getResponsiveSize('14px', '16px'),
                          border: '1px solid #ced4da',
                          borderRadius: '8px',
                          width: '100%',
                          boxSizing: 'border-box'
                        }}
                      >
                        {NOMBRES_SUCURSALES.map((sucursal, index) => (
                          <option key={index} value={sucursal}>{sucursal}</option>
                        ))}
                      </select>
                      
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: '8px', 
                          fontWeight: 'bold',
                          color: '#495057',
                          fontSize: getResponsiveSize('12px', '14px')
                        }}>
                          Imagen del producto:
                        </label>
                        
                        {/* Vista previa de la imagen */}
                        {editandoProducto.imagenURL && (
                          <div style={{ 
                            marginBottom: '15px', 
                            textAlign: 'center',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            padding: '10px'
                          }}>
                            <img 
                              src={editandoProducto.imagenURL} 
                              alt="Vista previa" 
                              style={{ 
                                maxHeight: '150px', 
                                maxWidth: '100%',
                                borderRadius: '4px'
                              }}
                            />
                            {editandoProducto.imagenURL.startsWith('blob:') && (
                              <div style={{
                                fontSize: '12px',
                                color: '#dc3545',
                                marginTop: '5px'
                              }}>
                                Imagen temporal - Se subirá al guardar
                              </div>
                            )}
                          </div>
                        )}
                        
                        <input
                          type="text"
                          placeholder="URL de la imagen (opcional)"
                          value={editandoProducto.imagenURL && !editandoProducto.imagen ? editandoProducto.imagenURL : ''}
                          onChange={manejarImagenURLEdicion}
                          style={{ 
                            marginBottom: '8px', 
                            width: '100%',
                            padding: getResponsiveSize('10px', '12px'),
                            fontSize: getResponsiveSize('14px', '16px'),
                            border: '1px solid #ced4da',
                            borderRadius: '8px',
                            boxSizing: 'border-box'
                          }}
                        />
                        <div style={{ 
                          textAlign: 'center', 
                          margin: '8px 0',
                          color: '#6c757d',
                          fontSize: getResponsiveSize('12px', '14px')
                        }}>
                          O
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={manejarImagenEdicion}
                          style={{ 
                            padding: getResponsiveSize('10px', '12px'),
                            fontSize: getResponsiveSize('14px', '16px'),
                            border: '1px solid #ced4da',
                            borderRadius: '8px',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        />
                        {editandoProducto.imagen && (
                          <div style={{ 
                            marginTop: '8px', 
                            fontSize: getResponsiveSize('12px', '14px'),
                            color: '#28a745'
                          }}>
                            ✓ Archivo seleccionado: {editandoProducto.imagen.name}
                          </div>
                        )}
                      </div>
                      
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr 1fr',
                        gap: '10px' 
                      }}>
                        <button 
                          onClick={guardarEdicionProducto}
                          style={{
                            padding: getResponsiveSize('12px', '15px'),
                            fontSize: getResponsiveSize('13px', '16px'),
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            transition: 'background-color 0.3s'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#218838'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
                        >
                          ✅ Guardar
                        </button>
                        <button 
                          onClick={() => setEditandoProducto(null)}
                          style={{
                            padding: getResponsiveSize('12px', '15px'),
                            fontSize: getResponsiveSize('13px', '16px'),
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            transition: 'background-color 0.3s'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c82333'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
                        >
                          ❌ Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                      {p.imagenURL && 
                        <div style={{ 
                          width: '100%',
                          height: window.innerWidth < 768 ? '100px' : '150px',
                          marginBottom: '12px',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          backgroundColor: '#f8f9fa'
                        }}>
                          <img 
                            src={p.imagenURL} 
                            alt={p.nombre} 
                            onError={handleImageError}
                            loading="lazy"
                            style={{ 
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        </div>
                      }
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <h4 style={{ 
                          margin: '0 0 8px 0', 
                          fontSize: getResponsiveSize('14px', '16px'),
                          fontWeight: 'bold',
                          color: '#333',
                          lineHeight: '1.3',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: window.innerWidth < 768 ? 'nowrap' : 'normal'
                        }}>
                          {p.nombre}
                        </h4>
                        
                        <div style={{ marginBottom: '8px' }}>
                          <p style={{ 
                            margin: '0 0 4px 0',
                            fontSize: getResponsiveSize('12px', '14px'),
                            color: '#6c757d'
                          }}>
                            📦 {p.cantidad !== null ? `${p.cantidad} unidades` : 'Stock no definido'}
                          </p>
                          <p style={{ 
                            margin: '0 0 8px 0', 
                            color: '#007bff', 
                            fontWeight: 'bold',
                            fontSize: getResponsiveSize('14px', '16px')
                          }}>
                            💰 ${formatNumber(p.precio)}
                          </p>
                        </div>
                        
                        <div style={{ marginBottom: '12px' }}>
                          {p.categoria && (
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 8px',
                              backgroundColor: '#e7f3ff',
                              color: '#0066cc',
                              borderRadius: '12px',
                              fontSize: getResponsiveSize('10px', '12px'),
                              marginBottom: '6px',
                              marginRight: '6px',
                              fontWeight: '500'
                            }}>
                              🏷️ {p.categoria}
                            </span>
                          )}
                          {p.sucursalOrigen && (
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 8px',
                              backgroundColor: '#d4edda',
                              borderRadius: '12px',
                              fontSize: getResponsiveSize('10px', '12px'),
                              color: '#155724',
                              fontWeight: '500'
                            }}>
                              📍 {p.sucursalOrigen}
                            </span>
                          )}
                        </div>
                        
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: '1fr 1fr',
                          gap: '8px',
                          marginTop: 'auto'
                        }}>
                          <button 
                            onClick={() => iniciarEdicionProducto(p, i)}
                            style={{
                              padding: getResponsiveSize('8px', '10px'),
                              backgroundColor: '#007bff',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: getResponsiveSize('12px', '14px'),
                              fontWeight: '500',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0056b3'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#007bff'}
                          >
                            ✏️ Editar
                          </button>
                          <button 
                            onClick={() => eliminarProducto(p)}
                            style={{ 
                              padding: getResponsiveSize('8px', '10px'),
                              backgroundColor: '#dc3545', 
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: getResponsiveSize('12px', '14px'),
                              fontWeight: '500',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c82333'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
                          >
                            🗑️ Eliminar
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        );

      case 'categorias':
        return (
          <section style={{ padding: window.innerWidth < 768 ? '10px' : '20px' }}>
            <h3 style={{ 
              fontSize: window.innerWidth < 768 ? '20px' : '24px',
              marginBottom: '20px',
              color: '#333',
              borderBottom: '2px solid #dee2e6',
              paddingBottom: '10px'
            }}>
              🏷️ Gestión de Categorías
            </h3>
            
            {/* Formulario para agregar nueva categoría */}
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: window.innerWidth < 768 ? '15px' : '20px',
              borderRadius: '12px',
              marginBottom: '25px',
              border: '1px solid #dee2e6',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h4 style={{ 
                margin: '0 0 15px 0', 
                color: '#007bff',
                fontSize: window.innerWidth < 768 ? '16px' : '18px'
              }}>
                ➕ Agregar Nueva Categoría
              </h4>
              
              <form onSubmit={agregarCategoria} style={{ 
                display: 'flex', 
                gap: window.innerWidth < 768 ? '8px' : '10px', 
                alignItems: 'center',
                flexDirection: window.innerWidth < 480 ? 'column' : 'row'
              }}>
                <input
                  type="text"
                  placeholder="Nombre de la categoría"
                  value={nuevaCategoria}
                  onChange={(e) => setNuevaCategoria(e.target.value)}
                  required
                  style={{
                    padding: getResponsiveSize('10px', '15px'),
                    fontSize: getResponsiveSize('14px', '16px'),
                    border: '1px solid #ced4da',
                    borderRadius: '8px',
                    flex: 1,
                    width: window.innerWidth < 480 ? '100%' : 'auto',
                    boxSizing: 'border-box'
                  }}
                />
                <button
                  type="submit"
                  disabled={cargandoCategorias}
                  style={{
                    padding: getResponsiveSize('10px 14px', '15px 20px'),
                    backgroundColor: cargandoCategorias ? '#6c757d' : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: cargandoCategorias ? 'not-allowed' : 'pointer',
                    fontSize: getResponsiveSize('14px', '16px'),
                    fontWeight: '500',
                    width: window.innerWidth < 480 ? '100%' : 'auto',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {cargandoCategorias ? 'Cargando...' : 'Agregar'}
                </button>
              </form>
            </div>

            {/* Lista de categorías existentes */}
            <div style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              border: '1px solid #dee2e6',
              overflow: 'hidden',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <div style={{
                backgroundColor: '#f8f9fa',
                padding: window.innerWidth < 768 ? '12px 15px' : '15px 20px',
                borderBottom: '1px solid #dee2e6',
                fontSize: window.innerWidth < 768 ? '16px' : '18px',
                fontWeight: '600',
                color: '#495057'
              }}>
                📋 Categorías Existentes ({categorias.length})
              </div>
              
              {cargandoCategorias ? (
                <div style={{ 
                  padding: '40px', 
                  textAlign: 'center', 
                  color: '#6c757d' 
                }}>
                  Cargando categorías...
                </div>
              ) : categorias.length === 0 ? (
                <div style={{ 
                  padding: '40px', 
                  textAlign: 'center', 
                  color: '#6c757d' 
                }}>
                  No hay categorías registradas
                </div>
              ) : (
                <div style={{ padding: '20px' }}>
                  {categorias.map((categoria, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: window.innerWidth < 480 ? 'flex-start' : 'center',
                        justifyContent: 'space-between',
                        flexDirection: window.innerWidth < 480 ? 'column' : 'row',
                        padding: getResponsiveSize('12px', '15px'),
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #dee2e6',
                        borderRadius: '8px',
                        marginBottom: getResponsiveSize('8px', '10px'),
                        gap: window.innerWidth < 480 ? '10px' : '0'
                      }}
                    >
                      {editandoCategoria === categoria ? (
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: getResponsiveSize('6px', '10px'), 
                          flex: 1,
                          flexDirection: window.innerWidth < 480 ? 'column' : 'row'
                        }}>
                          <input
                            type="text"
                            defaultValue={categoria}
                            style={{
                              padding: getResponsiveSize('6px 10px', '8px 12px'),
                              border: '1px solid #ced4da',
                              borderRadius: '6px',
                              flex: 1,
                              fontSize: getResponsiveSize('13px', '14px'),
                              width: window.innerWidth < 480 ? '100%' : 'auto'
                            }}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                editarCategoria(categoria, e.target.value);
                              }
                            }}
                            autoFocus
                          />
                          <div style={{
                            display: 'flex',
                            gap: '4%',
                            width: window.innerWidth < 480 ? '100%' : 'auto',
                            justifyContent: window.innerWidth < 480 ? 'space-between' : 'flex-start'
                          }}>
                            <button
                            onClick={(e) => {
                              const input = e.target.previousElementSibling;
                              editarCategoria(categoria, input.value);
                            }}
                            style={{
                              padding: getResponsiveSize('6px 10px', '8px 12px'),
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: getResponsiveSize('11px', '12px'),
                              width: window.innerWidth < 480 ? '48%' : 'auto'
                            }}
                          >
                            Guardar
                          </button>
                          <button
                            onClick={() => setEditandoCategoria(null)}
                            style={{
                              padding: getResponsiveSize('6px 10px', '8px 12px'),
                              backgroundColor: '#6c757d',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: getResponsiveSize('11px', '12px'),
                              width: window.innerWidth < 480 ? '48%' : 'auto'
                            }}
                          >
                            Cancelar
                          </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <span style={{
                            fontSize: window.innerWidth < 768 ? '14px' : '16px',
                            color: '#495057',
                            fontWeight: '500'
                          }}>
                            {categoria}
                          </span>
                          <div style={{ 
                            display: 'flex', 
                            gap: getResponsiveSize('6px', '8px'),
                            width: window.innerWidth < 480 ? '100%' : 'auto',
                            justifyContent: window.innerWidth < 480 ? 'space-between' : 'flex-start'
                          }}>
                            <button
                              onClick={() => setEditandoCategoria(categoria)}
                              style={{
                                padding: getResponsiveSize('5px 8px', '6px 12px'),
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: getResponsiveSize('11px', '12px'),
                                flex: window.innerWidth < 480 ? '1' : 'none'
                              }}
                            >
                              ✏️ Editar
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm(`¿Estás seguro de que quieres eliminar la categoría "${categoria}"?`)) {
                                  eliminarCategoria(categoria);
                                }
                              }}
                              style={{
                                padding: getResponsiveSize('5px 8px', '6px 12px'),
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: getResponsiveSize('11px', '12px'),
                                flex: window.innerWidth < 480 ? '1' : 'none'
                              }}
                            >
                              🗑️ Eliminar
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        );

      case 'usuarios':
        return (
          <section style={{ 
            padding: window.innerWidth < 768 ? '10px' : '20px',
            maxWidth: '100%'
          }}>
            <h3 style={{ 
              fontSize: window.innerWidth < 768 ? '20px' : '24px',
              marginBottom: '20px',
              color: '#333',
              borderBottom: '2px solid #dee2e6',
              paddingBottom: '10px'
            }}>
              👥 Gestión de Usuarios
            </h3>
            
            {/* Formulario de agregar usuario con mejor diseño móvil */}
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: window.innerWidth < 768 ? '15px' : '20px',
              borderRadius: '12px',
              marginBottom: '25px',
              border: '1px solid #dee2e6',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h4 style={{ 
                margin: '0 0 15px 0', 
                color: '#007bff',
                fontSize: window.innerWidth < 768 ? '16px' : '18px'
              }}>
                ➕ Agregar Nuevo Usuario
              </h4>
              
              <form onSubmit={agregarVendedor}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr',
                  gap: '15px', 
                  marginBottom: '15px' 
                }}>
                  <input
                    id="vendedor-username"
                    name="username"
                    type="text"
                    placeholder="Nombre de usuario"
                    value={nuevoVendedor.username}
                    onChange={(e) => setNuevoVendedor({...nuevoVendedor, username: e.target.value})}
                    required
                    style={{
                      padding: getResponsiveSize('12px', '15px'),
                      fontSize: getResponsiveSize('14px', '16px'),
                      border: '1px solid #ced4da',
                      borderRadius: '8px',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  />
                  <input
                    id="vendedor-password"
                    name="password"
                    type="password"
                    placeholder="Contraseña"
                    value={nuevoVendedor.password}
                    onChange={(e) => setNuevoVendedor({...nuevoVendedor, password: e.target.value})}
                    required
                    style={{
                      padding: getResponsiveSize('12px', '15px'),
                      fontSize: getResponsiveSize('14px', '16px'),
                      border: '1px solid #ced4da',
                      borderRadius: '8px',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr',
                  gap: '15px', 
                  marginBottom: '15px' 
                }}>
                  <div>
                    <label 
                      htmlFor="vendedor-rol"
                      style={{ 
                        display: 'block', 
                        marginBottom: '8px', 
                        fontWeight: 'bold',
                        color: '#495057',
                        fontSize: getResponsiveSize('14px', '16px')
                      }}>
                      Rol:
                    </label>
                    <select
                      id="vendedor-rol"
                      name="rol"
                      value={nuevoVendedor.rol}
                      onChange={(e) => setNuevoVendedor({...nuevoVendedor, rol: e.target.value})}
                      required
                      style={{ 
                        padding: getResponsiveSize('12px', '15px'),
                        fontSize: getResponsiveSize('14px', '16px'),
                        border: '1px solid #ced4da',
                        borderRadius: '8px',
                        width: '100%',
                        boxSizing: 'border-box'
                      }}
                    >
                      <option value="vendedor">Vendedor</option>
                      <option value="admin">Administrador</option>
                      <option value="mesero">Mesero</option>
                    </select>
                  </div>

                  {/* Selector de sucursal - solo para vendedores y meseros */}
                  {(nuevoVendedor.rol === 'vendedor' || nuevoVendedor.rol === 'mesero') && (
                    <div>
                      <label 
                        htmlFor="vendedor-sucursal"
                        style={{ 
                          display: 'block', 
                          marginBottom: '8px', 
                          fontWeight: 'bold',
                          color: '#495057',
                          fontSize: getResponsiveSize('14px', '16px')
                        }}>
                        Sucursal:
                      </label>
                      <select
                        id="vendedor-sucursal"
                        name="sucursal"
                        value={nuevoVendedor.sucursal}
                        onChange={(e) => setNuevoVendedor({...nuevoVendedor, sucursal: e.target.value})}
                        required
                        style={{ 
                          padding: getResponsiveSize('12px', '15px'),
                          fontSize: getResponsiveSize('14px', '16px'),
                          border: '1px solid #ced4da',
                          borderRadius: '8px',
                          width: '100%',
                          boxSizing: 'border-box'
                        }}
                      >
                        {SUCURSALES_DISPONIBLES.map((sucursal, index) => (
                          <option key={index} value={sucursal}>{sucursal}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                
                  {/* Subida de foto de perfil con Base64 */}
                <div style={{ marginBottom: '20px' }}>
                  <label 
                    htmlFor="vendedor-imagen-input"
                    style={{ 
                      display: 'block', 
                      marginBottom: '12px', 
                      fontWeight: 'bold',
                      color: '#495057',
                      fontSize: getResponsiveSize('14px', '16px')
                    }}>
                    Foto de Perfil:
                  </label>
                  
                  {/* Preview de la imagen */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                    marginBottom: '10px'
                  }}>
                    <img 
                      src={nuevoVendedor.imagenURL}
                      alt="Vista previa"
                      style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '3px solid #dee2e6',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <input
                        id="vendedor-imagen-input"
                        name="vendedor-imagen"
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleVendedorImageChange(e, false)}
                        style={{
                          padding: '10px',
                          border: '2px dashed #28a745',
                          borderRadius: '8px',
                          backgroundColor: '#f8fff9',
                          width: '100%',
                          fontSize: getResponsiveSize('14px', '16px'),
                          cursor: 'pointer'
                        }}
                      />
                      <small style={{
                        color: '#28a745',
                        fontSize: '12px',
                        display: 'block',
                        marginTop: '5px',
                        fontWeight: 'bold'
                      }}>
                        ✅ Formato: JPG, PNG, GIF (máx. 2MB) - SIN Firebase Storage
                      </small>
                    </div>
                  </div>
                </div>
                
                <button 
                  type="submit"
                  style={{
                    padding: getResponsiveSize('15px', '18px'),
                    fontSize: getResponsiveSize('16px', '18px'),
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    width: '100%',
                    fontWeight: 'bold',
                    transition: 'background-color 0.3s ease',
                    boxShadow: '0 2px 4px rgba(40, 167, 69, 0.3)'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#218838'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
                >
                  ✅ Agregar Usuario
                </button>
              </form>
            </div>

            {/* Lista de usuarios mejorada */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '1px solid #dee2e6',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: window.innerWidth < 768 ? '15px' : '20px',
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #dee2e6'
              }}>
                <h4 style={{ 
                  margin: 0, 
                  color: '#007bff',
                  fontSize: window.innerWidth < 768 ? '16px' : '18px'
                }}>
                  📋 Lista de Usuarios ({vendedores.length})
                </h4>
              </div>
              
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: window.innerWidth < 768 
                  ? '1fr'
                  : 'repeat(auto-fill, minmax(420px, 1fr))',
                gap: window.innerWidth < 768 ? '0' : '20px',
                padding: window.innerWidth < 768 ? '0' : '20px'
              }}>
                {vendedores.map((v, i) => (
                  <div key={i} style={{
                    backgroundColor: window.innerWidth < 768 ? 'white' : '#f8f9fa',
                    border: window.innerWidth < 768 ? 'none' : '1px solid #dee2e6',
                    borderBottom: window.innerWidth < 768 ? '1px solid #dee2e6' : 'none',
                    borderRadius: window.innerWidth < 768 ? '0' : '8px',
                    padding: window.innerWidth < 768 ? '16px' : '20px',
                    transition: 'all 0.2s ease',
                    minHeight: window.innerWidth < 768 ? 'auto' : '140px',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    {editandoVendedor?.index === i ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        <h5 style={{ 
                          margin: '0 0 15px 0', 
                          color: '#007bff',
                          fontSize: getResponsiveSize('14px', '16px')
                        }}>
                          ✏️ Editando Usuario
                        </h5>
                        
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr',
                          gap: '15px'
                        }}>
                          <input
                            type="text"
                            value={editandoVendedor.username}
                            onChange={(e) => setEditandoVendedor({...editandoVendedor, username: e.target.value})}
                            placeholder="Nombre de usuario"
                            style={{
                              padding: getResponsiveSize('12px', '15px'),
                              fontSize: getResponsiveSize('14px', '16px'),
                              border: '1px solid #ced4da',
                              borderRadius: '8px',
                              width: '100%',
                              boxSizing: 'border-box'
                            }}
                          />
                          <input
                            type="password"
                            placeholder="Nueva contraseña"
                            value={editandoVendedor.password}
                            onChange={(e) => setEditandoVendedor({...editandoVendedor, password: e.target.value})}
                            style={{
                              padding: getResponsiveSize('12px', '15px'),
                              fontSize: getResponsiveSize('14px', '16px'),
                              border: '1px solid #ced4da',
                              borderRadius: '8px',
                              width: '100%',
                              boxSizing: 'border-box'
                            }}
                          />
                        </div>
                        
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr',
                          gap: '15px'
                        }}>
                          <select
                            value={editandoVendedor.rol}
                            onChange={(e) => setEditandoVendedor({...editandoVendedor, rol: e.target.value})}
                            style={{ 
                              padding: getResponsiveSize('12px', '15px'),
                              fontSize: getResponsiveSize('14px', '16px'),
                              border: '1px solid #ced4da',
                              borderRadius: '8px',
                              width: '100%',
                              boxSizing: 'border-box'
                            }}
                          >
                            <option value="vendedor">Vendedor</option>
                            <option value="admin">Administrador</option>
                            <option value="mesero">Mesero</option>
                          </select>

                          {/* Selector de sucursal para edición - solo para vendedores y meseros */}
                          {(editandoVendedor.rol === 'vendedor' || editandoVendedor.rol === 'mesero') && (
                            <select
                              value={editandoVendedor.sucursal || SUCURSALES_DISPONIBLES[0]}
                              onChange={(e) => setEditandoVendedor({...editandoVendedor, sucursal: e.target.value})}
                              required
                              style={{ 
                                padding: getResponsiveSize('12px', '15px'),
                                fontSize: getResponsiveSize('14px', '16px'),
                                border: '1px solid #ced4da',
                                borderRadius: '8px',
                                width: '100%',
                                boxSizing: 'border-box'
                              }}
                            >
                              {SUCURSALES_DISPONIBLES.map((sucursal, index) => (
                                <option key={index} value={sucursal}>{sucursal}</option>
                              ))}
                            </select>
                          )}
                        </div>
                        
                        {/* Subida de foto de perfil para edición */}
                        <div style={{ marginBottom: '15px' }}>
                          <label 
                            htmlFor="editar-vendedor-imagen"
                            style={{ 
                              display: 'block', 
                              marginBottom: '10px', 
                              fontWeight: 'bold',
                              color: '#495057',
                              fontSize: getResponsiveSize('12px', '14px')
                            }}>
                            Foto de Perfil:
                          </label>
                          
                          {/* Preview de la imagen */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '10px'
                          }}>
                            <img 
                              src={editandoVendedor.imagenURL || DEFAULT_PROFILE_IMAGE}
                              alt="Vista previa"
                              style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '50%',
                                objectFit: 'cover',
                                border: '2px solid #dee2e6',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                              }}
                            />
                            <div style={{ flex: 1 }}>
                              <input
                                id="editar-vendedor-imagen"
                                name="editar-vendedor-imagen"
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleVendedorImageChange(e, true)}
                                style={{
                                  padding: '8px',
                                  border: '2px dashed #28a745',
                                  borderRadius: '6px',
                                  backgroundColor: '#f8fff9',
                                  width: '100%',
                                  fontSize: getResponsiveSize('12px', '14px'),
                                  cursor: 'pointer'
                                }}
                              />
                              <small style={{
                                color: '#28a745',
                                fontSize: '10px',
                                display: 'block',
                                marginTop: '3px',
                                fontWeight: 'bold'
                              }}>
                                ✅ JPG, PNG, GIF (máx. 2MB)
                              </small>
                            </div>
                          </div>
                        </div>
                        
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: '1fr 1fr',
                          gap: '10px' 
                        }}>
                          <button 
                            onClick={guardarEdicionVendedor}
                            style={{
                              padding: getResponsiveSize('12px', '15px'),
                              fontSize: getResponsiveSize('14px', '16px'),
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: 'bold',
                              transition: 'background-color 0.3s'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#218838'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
                          >
                            ✅ Guardar
                          </button>
                          <button 
                            onClick={() => setEditandoVendedor(null)}
                            style={{
                              padding: getResponsiveSize('12px', '15px'),
                              fontSize: getResponsiveSize('14px', '16px'),
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: 'bold',
                              transition: 'background-color 0.3s'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c82333'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
                          >
                            ❌ Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: '12px',
                        height: '100%'
                      }}>
                        {/* Avatar y información principal */}
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '15px',
                          flex: '1'
                        }}>
                          {/* Imagen del vendedor con fallback */}
                          <img 
                            src={v.imagenURL || DEFAULT_PROFILE_IMAGE} 
                            alt={v.username} 
                            style={{ 
                              width: window.innerWidth < 768 ? '50px' : '60px', 
                              height: window.innerWidth < 768 ? '50px' : '60px', 
                              borderRadius: '50%', 
                              objectFit: 'cover',
                              border: '3px solid #007bff',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              flexShrink: 0
                            }}
                            onError={(e) => {
                              console.log("❌ Error cargando imagen para vendedor:", v.username, "URL:", v.imagenURL);
                              e.target.src = DEFAULT_PROFILE_IMAGE;
                            }}
                          />
                          
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <h5 style={{
                              margin: '0 0 8px 0',
                              fontSize: getResponsiveSize('15px', '16px'),
                              fontWeight: 'bold',
                              color: '#333',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              👤 {v.username}
                            </h5>
                            
                            <div style={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '6px'
                            }}>
                              <span style={{ 
                                padding: '4px 10px',
                                borderRadius: '12px',
                                backgroundColor: (v.rol || 'vendedor') === 'admin' ? '#dc3545' : 
                                               (v.rol || 'vendedor') === 'mesero' ? '#ffc107' : '#28a745',
                                color: (v.rol || 'vendedor') === 'mesero' ? '#000' : '#fff',
                                fontSize: getResponsiveSize('11px', '12px'),
                                fontWeight: 'bold',
                                display: 'inline-block',
                                alignSelf: 'flex-start',
                                maxWidth: '100%',
                                textAlign: 'center'
                              }}>
                                {(v.rol || 'vendedor') === 'admin' ? '👨‍💼 Admin' :
                                 (v.rol || 'vendedor') === 'mesero' ? '🍽️ Mesero' : '💼 Vendedor'}
                              </span>
                              
                              {/* Mostrar sucursal solo para vendedores y meseros */}
                              {((v.rol || 'vendedor') === 'vendedor' || (v.rol || 'vendedor') === 'mesero') && v.sucursal && (
                                <span style={{
                                  padding: '4px 10px',
                                  borderRadius: '12px',
                                  backgroundColor: '#e7f3ff',
                                  color: '#0066cc',
                                  fontSize: getResponsiveSize('11px', '12px'),
                                  fontWeight: '500',
                                  display: 'inline-block',
                                  alignSelf: 'flex-start',
                                  maxWidth: '100%',
                                  textAlign: 'center',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  📍 {v.sucursal}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Botones de acción */}
                        <div style={{ 
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '8px',
                          marginTop: 'auto'
                        }}>
                          <button 
                            onClick={() => iniciarEdicionVendedor(v, i)}
                            style={{
                              padding: getResponsiveSize('8px', '10px'),
                              backgroundColor: '#007bff',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: getResponsiveSize('12px', '13px'),
                              fontWeight: '500',
                              transition: 'background-color 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0056b3'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#007bff'}
                          >
                            <span>✏️</span>
                            <span>Editar</span>
                          </button>
                          <button 
                            onClick={() => eliminarVendedor(v)}
                            style={{
                              padding: getResponsiveSize('8px', '10px'),
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: getResponsiveSize('12px', '13px'),
                              fontWeight: '500',
                              transition: 'background-color 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c82333'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
                          >
                            <span>🗑️</span>
                            <span>Eliminar</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      case 'ventas':
        // Crear array con todos los métodos de pago y sus datos para mostrar
        const metodosPago = [
          { nombre: 'Efectivo', color: '#198754', ventas: ventasPorMetodoPago.efectivo, total: metodoPagoStats.efectivo },
          { nombre: 'Tarjeta', color: '#0d6efd', ventas: ventasPorMetodoPago.tarjeta, total: metodoPagoStats.tarjeta },
          { nombre: 'Transferencia', color: '#6f42c1', ventas: ventasPorMetodoPago.transferencia, total: metodoPagoStats.transferencia }
        ];
        
        return (
          <div style={{ padding: '20px' }}>
            <div 
              className={window.innerWidth < 768 ? 'date-filters' : ''}
              style={{ 
                marginBottom: '20px', 
                display: 'flex', 
                gap: window.innerWidth < 768 ? '8px' : '10px', 
                alignItems: 'center', 
                flexWrap: 'wrap',
                padding: window.innerWidth < 768 ? '15px' : '0',
                backgroundColor: window.innerWidth < 768 ? '#f8f9fa' : 'transparent',
                borderRadius: window.innerWidth < 768 ? '8px' : '0',
                border: window.innerWidth < 768 ? '1px solid #dee2e6' : 'none'
              }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: window.innerWidth < 768 ? '5px' : '10px',
                flex: window.innerWidth < 768 ? '1 1 100%' : 'none',
                minWidth: window.innerWidth < 768 ? '100%' : 'auto'
              }}>
                <label style={{ 
                  fontSize: window.innerWidth < 768 ? '14px' : '16px',
                  fontWeight: 'bold',
                  color: '#8B4513',
                  minWidth: 'fit-content'
                }}>
                  Desde:
                </label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  style={{
                    padding: window.innerWidth < 768 ? '6px' : '8px',
                    borderRadius: '6px',
                    border: '2px solid #D2B48C',
                    fontSize: window.innerWidth < 768 ? '14px' : '16px',
                    flex: 1,
                    minWidth: window.innerWidth < 768 ? '120px' : 'auto'
                  }}
                />
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: window.innerWidth < 768 ? '5px' : '10px',
                flex: window.innerWidth < 768 ? '1 1 100%' : 'none',
                minWidth: window.innerWidth < 768 ? '100%' : 'auto'
              }}>
                <label style={{ 
                  fontSize: window.innerWidth < 768 ? '14px' : '16px',
                  fontWeight: 'bold',
                  color: '#8B4513',
                  minWidth: 'fit-content'
                }}>
                  Hasta:
                </label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  style={{
                    padding: window.innerWidth < 768 ? '6px' : '8px',
                    borderRadius: '6px',
                    border: '2px solid #D2B48C',
                    fontSize: window.innerWidth < 768 ? '14px' : '16px',
                    flex: 1,
                    minWidth: window.innerWidth < 768 ? '120px' : 'auto'
                  }}
                />
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: window.innerWidth < 768 ? '5px' : '10px',
                flex: window.innerWidth < 768 ? '1 1 100%' : 'none',
                minWidth: window.innerWidth < 768 ? '100%' : 'auto'
              }}>
                <label style={{ 
                  fontSize: window.innerWidth < 768 ? '14px' : '16px',
                  fontWeight: 'bold',
                  color: '#8B4513',
                  minWidth: 'fit-content'
                }}>
                  Sucursal:
                </label>
                <select
                  value={filtroSucursal}
                  onChange={(e) => setFiltroSucursal(e.target.value)}
                  style={{
                    padding: window.innerWidth < 768 ? '6px' : '8px',
                    borderRadius: '6px',
                    border: '2px solid #D2B48C',
                    fontSize: window.innerWidth < 768 ? '14px' : '16px',
                    flex: 1,
                    minWidth: window.innerWidth < 768 ? '120px' : '150px'
                  }}
                >
                  <option value="Todas">Todas las sucursales</option>
                  {SUCURSALES_DISPONIBLES.map((sucursal, index) => (
                    <option key={index} value={sucursal}>{sucursal}</option>
                  ))}
                </select>
              </div>
              <button
                style={{
                  padding: window.innerWidth < 768 ? '8px 12px' : '8px 16px',
                  backgroundColor: '#8B4513',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: window.innerWidth < 768 ? '14px' : '16px',
                  fontWeight: 'bold',
                  flex: window.innerWidth < 768 ? '1 1 100%' : 'none',
                  minWidth: window.innerWidth < 768 ? '100%' : 'auto',
                  marginTop: window.innerWidth < 768 ? '10px' : '0'
                }}
              >
                🔄 Actualizar
              </button>
            </div>

            <h2 style={{ 
              fontSize: window.innerWidth < 768 ? '20px' : '24px',
              marginBottom: '20px',
              color: '#333',
              textAlign: window.innerWidth < 768 ? 'center' : 'left'
            }}>
              📊 Panel de Ventas
            </h2>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(2, 1fr)', 
              gap: window.innerWidth < 768 ? '15px' : '20px',
              marginBottom: '30px' 
            }}>
              <div style={{ 
                padding: window.innerWidth < 768 ? '15px' : '20px', 
                backgroundColor: '#007bff', 
                color: 'white', 
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 4px 8px rgba(0, 123, 255, 0.3)',
                border: '2px solid #0056b3'
              }}>
                <h3 style={{ 
                  margin: '0 0 10px 0',
                  fontSize: window.innerWidth < 768 ? '16px' : '18px'
                }}>
                  💰 Ventas del Día
                </h3>
                <h2 style={{ 
                  margin: 0,
                  fontSize: window.innerWidth < 768 ? '24px' : '32px',
                  fontWeight: 'bold'
                }}>
                  ${formatNumber(totalDelDia)}
                </h2>
              </div>
              <div style={{ 
                padding: window.innerWidth < 768 ? '15px' : '20px', 
                backgroundColor: '#28a745', 
                color: 'white', 
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 4px 8px rgba(40, 167, 69, 0.3)',
                border: '2px solid #218838'
              }}>
                <h3 style={{ 
                  margin: '0 0 10px 0',
                  fontSize: window.innerWidth < 768 ? '16px' : '18px'
                }}>
                  📊 Total de Ventas
                </h3>
                <h2 style={{ 
                  margin: 0,
                  fontSize: window.innerWidth < 768 ? '24px' : '32px',
                  fontWeight: 'bold'
                }}>
                  {formatNumber(ventasDelDia.length)}
                </h2>
              </div>
            </div>

            {/* Métodos de Pago Section */}
            <h3 style={{ 
              fontSize: window.innerWidth < 768 ? '18px' : '20px',
              color: '#8B4513',
              borderBottom: '2px solid #D2B48C',
              paddingBottom: '8px',
              marginBottom: '20px'
            }}>
              💳 Ventas por Método de Pago
            </h3>
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: window.innerWidth < 768 ? '1fr' : window.innerWidth < 1024 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
              gap: window.innerWidth < 768 ? '12px' : '15px',
              marginBottom: '30px'
            }}>
              {metodosPago.map((metodo, index) => (
                <div key={index} style={{ 
                  padding: window.innerWidth < 768 ? '12px' : '15px',
                  border: `2px solid ${metodo.color}`,
                  borderRadius: '10px',
                  backgroundColor: metodo.color,
                  color: 'white',
                  textAlign: 'center',
                  boxShadow: '0 3px 6px rgba(0,0,0,0.2)'
                }}>
                  <h4 style={{ 
                    margin: '0 0 8px 0',
                    fontSize: window.innerWidth < 768 ? '14px' : '16px'
                  }}>
                    {metodo.nombre}
                  </h4>
                  <p style={{ 
                    margin: '5px 0', 
                    fontSize: window.innerWidth < 768 ? '20px' : '24px', 
                    fontWeight: 'bold' 
                  }}>
                    ${formatNumber(metodo.total)}
                  </p>
                  <p style={{ 
                    margin: 0,
                    fontSize: window.innerWidth < 768 ? '12px' : '14px',
                    opacity: 0.9
                  }}>
                    ({metodo.ventas.length} ventas)
                  </p>
                </div>
              ))}
            </div>

            {/* Gráfica de Barras de Ventas por Día */}
            {ventasDelDia.length > 0 && (
              <>
                <h3 style={{ 
                  fontSize: window.innerWidth < 768 ? '18px' : '20px',
                  color: '#8B4513',
                  borderBottom: '2px solid #D2B48C',
                  paddingBottom: '8px',
                  marginBottom: '20px'
                }}>
                  📈 Gráfico de Ventas por Día
                </h3>
                <div style={{
                  backgroundColor: 'white',
                  border: '2px solid #D2B48C',
                  borderRadius: '12px',
                  padding: window.innerWidth < 768 ? '15px' : '20px',
                  marginBottom: '30px',
                  boxShadow: '0 4px 8px rgba(139, 69, 19, 0.2)',
                  position: 'relative'
                }}>
                  {/* Información del rango y total */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '15px',
                    padding: '10px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}>
                    <span>
                      📅 <strong>Período:</strong> {dateRange.start} - {dateRange.end}
                    </span>
                    <span>
                      💰 <strong>Total:</strong> ${formatNumber(agruparVentasPorDia(ventasDelDia).reduce((sum, dia) => sum + dia.total, 0))} | 
                      📊 <strong>Ventas:</strong> {agruparVentasPorDia(ventasDelDia).reduce((sum, dia) => sum + dia.cantidad, 0)}
                    </span>
                  </div>

                  {/* Contenedor con scroll horizontal controlado */}
                  <div style={{
                    width: '100%',
                    maxWidth: '100%',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    border: '1px solid #D2B48C', // Color café claro
                    borderRadius: '6px',
                    backgroundColor: '#FEFCF8' // Fondo crema suave
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'end',
                      justifyContent: 'flex-start',
                      height: '350px',
                      borderBottom: '2px solid #8B4513', // Color café
                      borderLeft: '2px solid #8B4513', // Color café
                      padding: '20px 20px 40px 60px',
                      position: 'relative',
                      minWidth: `${Math.max(600, agruparVentasPorDia(ventasDelDia).length * 64)}px`, // Ajustado: 60px + 4px margin
                      width: 'fit-content'
                    }}>
                    {/* Líneas de referencia del eje Y con valores en dinero */}
                    {(() => {
                      const ventasPorDia = agruparVentasPorDia(ventasDelDia);
                      const maxTotal = Math.max(...ventasPorDia.map(v => v.total), 1);
                      const step = Math.ceil(maxTotal / 5);
                      
                      return [0, step, step * 2, step * 3, step * 4, step * 5].map((valor, index) => (
                        <div
                          key={index}
                          style={{
                            position: 'absolute',
                            left: '60px',
                            right: '20px',
                            bottom: `${40 + (index * 52)}px`,
                            borderTop: index === 0 ? 'none' : '1px dashed #D2B48C', // Color café claro
                            fontSize: '12px',
                            color: '#8B4513' // Color café
                          }}
                        >
                          <span style={{
                            position: 'absolute',
                            left: '-55px',
                            top: '-8px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            color: '#8B4513' // Color café
                          }}>
                            ${formatNumber(valor)}
                          </span>
                        </div>
                      ));
                    })()}
                    
                    {agruparVentasPorDia(ventasDelDia).map((dia, index) => {
                      const ventasPorDia = agruparVentasPorDia(ventasDelDia);
                      const maxTotal = Math.max(...ventasPorDia.map(v => v.total), 1);
                      const heightPercentage = (dia.total / maxTotal) * 100;
                      const barHeight = (heightPercentage * 260) / 100; // 260px es la altura máxima disponible
                      
                      return (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            height: '100%',
                            minWidth: '60px', // Reducido para ajustar más barras
                            justifyContent: 'flex-end',
                            margin: '0 2px' // Añadido margen entre barras
                          }}
                        >
                          <div
                            style={{
                              width: '40px', // Reducido para ajustar más barras
                              height: `${barHeight}px`,
                              backgroundColor: '#8B4513', // Color café para coincidir con el login
                              borderRadius: '4px 4px 0 0',
                              marginBottom: '0px',
                              position: 'relative',
                              transition: 'all 0.3s ease',
                              cursor: 'pointer',
                              boxShadow: '0 2px 4px rgba(139, 69, 19, 0.3)',
                              border: '1px solid #A0522D'
                            }}
                            title={`${dia.fecha}: $${formatNumber(dia.total)} (${dia.cantidad} ventas)`}
                            onMouseOver={(e) => {
                              e.currentTarget.style.backgroundColor = '#A0522D';
                              e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.backgroundColor = '#8B4513';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          >
                            <div style={{
                              position: 'absolute',
                              top: '-35px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              color: '#333',
                              whiteSpace: 'nowrap',
                              backgroundColor: 'rgba(255,255,255,0.9)',
                              padding: '2px 4px',
                              borderRadius: '3px',
                              border: '1px solid #ddd'
                            }}>
                              ${formatNumber(dia.total)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    </div>
                    
                    {/* Etiquetas de fechas en el eje X - dentro del scroll */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      marginTop: '10px',
                      marginLeft: '60px',
                      marginRight: '20px',
                      minWidth: `${Math.max(600, agruparVentasPorDia(ventasDelDia).length * 64)}px`, // Coincide con el gráfico
                      width: 'fit-content'
                    }}>
                      {agruparVentasPorDia(ventasDelDia).map((dia, index) => (
                        <div
                          key={index}
                          style={{
                            minWidth: '60px', // Ajustado para coincidir con las barras
                            textAlign: 'center',
                            fontSize: '10px', // Reducido para ajustar más texto
                            fontWeight: 'bold',
                            color: '#8B4513', // Color café
                            padding: '5px 2px',
                            margin: '0 2px' // Mismo margen que las barras
                          }}
                        >
                          {dia.fecha}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Indicador de scroll */}
                  <div style={{
                    textAlign: 'center',
                    marginTop: '10px',
                    fontSize: '12px',
                    color: '#666',
                    fontStyle: 'italic'
                  }}>
                    {agruparVentasPorDia(ventasDelDia).length > 10 && 
                      "⬅️ Desplázate horizontalmente para ver más datos ➡️"
                    }
                  </div>
                  
                  {/* Eje X label */}
                  <div style={{
                    textAlign: 'center',
                    marginTop: '15px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: '#333'
                  }}>
                    📅 Días
                  </div>
                </div>
              </>
            )}
            
            {/* Detalles de Ventas por Método de Pago */}
            <div style={{ marginBottom: '30px' }}>
              {metodosPago.filter(metodo => metodo.ventas.length > 0).map((metodo, index) => (
                <div key={index} style={{ marginBottom: '30px' }}>
                  <h3 style={{ 
                    color: metodo.color, 
                    borderBottom: `2px solid ${metodo.color}`,
                    paddingBottom: '8px'
                  }}>
                    Ventas por {metodo.nombre}
                  </h3>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {metodo.ventas.map(venta => (
                      <div key={venta.id} style={{ 
                        padding: '10px', 
                        border: `1px solid ${metodo.color}`,
                        marginBottom: '10px',
                        borderRadius: '4px',
                        backgroundColor: 'white'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <p style={{ margin: '5px 0' }}><strong>Total:</strong> ${formatNumber(venta.total)}</p>
                            {venta.mesero ? (
                              <>
                                <p style={{ margin: '5px 0' }}><strong>Mesero:</strong> {venta.mesero}</p>
                                <p style={{ margin: '5px 0' }}><strong>Procesado por:</strong> {venta.vendedorNombre}</p>
                              </>
                            ) : (
                              <p style={{ margin: '5px 0' }}><strong>Vendedor:</strong> {venta.vendedorNombre}</p>
                            )}
                            <p style={{ margin: '5px 0' }}><strong>Fecha:</strong> {venta.fecha.toLocaleString()}</p>
                            {venta.sucursal && <p style={{ margin: '5px 0' }}><strong>Sucursal:</strong> {venta.sucursal}</p>}
                            {venta.mesa && <p style={{ margin: '5px 0' }}><strong>Mesa:</strong> {venta.mesa}</p>}
                          </div>
                          <div style={{
                            backgroundColor: metodo.color,
                            color: 'white',
                            padding: '3px 8px',
                            borderRadius: '12px',
                            fontSize: '0.8rem'
                          }}>
                            {metodo.nombre}
                          </div>
                        </div>
                        
                        <div style={{ 
                          marginTop: '10px', 
                          borderTop: '1px solid #eee',
                          paddingTop: '10px'
                        }}>
                          <p style={{ margin: '5px 0', fontWeight: 'bold' }}>Productos:</p>
                          <ul style={{ margin: 0, paddingLeft: '20px' }}>
                            {venta.productos && venta.productos.map((prod, i) => (
                              <li key={i} style={{ marginBottom: '8px', padding: '5px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                                  <div>
                                    <strong>{prod.nombre}</strong> - {prod.cantidadVenta} x ${formatNumber(prod.precio)} 
                                    = <span style={{ color: '#28a745', fontWeight: 'bold' }}>${formatNumber(prod.precio * prod.cantidadVenta)}</span>
                                  </div>
                                  {prod.sucursalOrigen && (
                                    <span style={{
                                      display: 'inline-block',
                                      padding: '2px 8px',
                                      backgroundColor: '#8B4513',
                                      color: 'white',
                                      borderRadius: '12px',
                                      fontSize: '0.75rem',
                                      fontWeight: 'bold'
                                    }}>
                                      📍 {prod.sucursalOrigen}
                                    </span>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Top Productos Section */}
            <h3>Top 3 Productos Más Vendidos</h3>
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(3, 1fr)',
              gap: '20px',
              marginBottom: '30px'
            }}>
              {topProductos.map((producto) => (
                <div key={producto.id} style={{ 
                  padding: '15px',
                  backgroundColor: '#fff',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px'
                }}>
                  {producto.imagenURL && (
                    <img 
                      src={producto.imagenURL}
                      alt={producto.nombre}
                      style={{
                        width: '100%',
                        height: '80px', // Reduced from 100px
                        objectFit: 'contain', // Changed from 'cover' to 'contain'
                        borderRadius: '4px',
                        backgroundColor: '#f8f9fa' // Added background for better visibility
                      }}
                    />
                  )}
                  <h4 style={{ margin: 0 }}>{producto.nombre}</h4>
                  <p style={{ margin: 0 }}>Cantidad vendida: {formatNumber(producto.cantidadTotal)}</p>
                  <p style={{ margin: 0, color: '#007bff', fontWeight: 'bold' }}>
                    ${formatNumber(producto.ventasTotal)} en total
                  </p>
                </div>
              ))}
            </div>

            <h3>Últimas Ventas</h3>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {[...ventasDelDia].reverse().slice(0, 5).map(venta => (
                <div key={venta.id} style={{ 
                  padding: '15px', 
                  border: '1px solid #dee2e6',
                  marginBottom: '15px',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ marginBottom: '10px' }}>
                    <p style={{ margin: '5px 0', fontSize: '18px', fontWeight: 'bold', color: '#28a745' }}>
                      Total: ${formatNumber(venta.total)}
                    </p>
                    {venta.mesero ? (
                      <>
                        <p style={{ margin: '3px 0' }}><strong>Mesero:</strong> {venta.mesero}</p>
                        <p style={{ margin: '3px 0' }}><strong>Procesado por:</strong> {venta.vendedorNombre}</p>
                      </>
                    ) : (
                      <p style={{ margin: '3px 0' }}><strong>Vendedor:</strong> {venta.vendedorNombre}</p>
                    )}
                    <p style={{ margin: '3px 0' }}><strong>Hora:</strong> {venta.fecha.toLocaleTimeString()}</p>
                    <p style={{ margin: '3px 0' }}><strong>Método de Pago:</strong> {venta.metodoPago || 'No especificado'}</p>
                    {venta.sucursal && <p style={{ margin: '3px 0' }}><strong>Sucursal de Venta:</strong> {venta.sucursal}</p>}
                    {venta.mesa && <p style={{ margin: '3px 0' }}><strong>Mesa:</strong> {venta.mesa}</p>}
                  </div>
                  
                  {/* Productos vendidos con sucursal de origen */}
                  {venta.productos && venta.productos.length > 0 && (
                    <div style={{ 
                      borderTop: '1px solid #eee',
                      paddingTop: '10px',
                      marginTop: '10px'
                    }}>
                      <p style={{ margin: '5px 0', fontWeight: 'bold', color: '#8B4513' }}>Productos Vendidos:</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {venta.productos.map((prod, i) => (
                          <div key={i} style={{ 
                            padding: '8px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '6px',
                            border: '1px solid #e9ecef'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                              <div>
                                <strong style={{ color: '#2c3e50' }}>{prod.nombre}</strong>
                                <div style={{ fontSize: '14px', color: '#666' }}>
                                  {prod.cantidadVenta} x ${formatNumber(prod.precio)} = 
                                  <span style={{ color: '#28a745', fontWeight: 'bold', marginLeft: '5px' }}>
                                    ${formatNumber(prod.precio * prod.cantidadVenta)}
                                  </span>
                                </div>
                              </div>
                              {prod.sucursalOrigen && (
                                <span style={{
                                  display: 'inline-block',
                                  padding: '4px 10px',
                                  backgroundColor: '#8B4513',
                                  color: 'white',
                                  borderRadius: '15px',
                                  fontSize: '0.8rem',
                                  fontWeight: 'bold'
                                }}>
                                  📍 {prod.sucursalOrigen}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      case 'mesas':
        return (
          <section style={{ padding: window.innerWidth < 768 ? '15px' : '20px' }}>
            <h3 style={{ 
              fontSize: window.innerWidth < 768 ? '20px' : '24px',
              marginBottom: '20px', 
              color: '#333',
              textAlign: window.innerWidth < 768 ? 'center' : 'left'
            }}>
              🪑 Gestión de Mesas
            </h3>
            
            {/* Formulario de agregar mesa con diseño unificado */}
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: window.innerWidth < 768 ? '15px' : '20px',
              borderRadius: '12px',
              marginBottom: '25px',
              border: '1px solid #dee2e6',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h4 style={{ 
                margin: '0 0 15px 0', 
                color: '#007bff',
                fontSize: window.innerWidth < 768 ? '16px' : '18px'
              }}>
                ➕ Agregar Nueva Mesa
              </h4>
              
              <form onSubmit={agregarMesa}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr',
                  gap: '15px', 
                  marginBottom: '15px' 
                }}>
                  <select
                    value={nuevaMesa.sucursal}
                    onChange={(e) => setNuevaMesa({ ...nuevaMesa, sucursal: e.target.value })}
                    required
                    style={{
                      padding: getResponsiveSize('12px', '15px'),
                      fontSize: getResponsiveSize('14px', '16px'),
                      border: '1px solid #ced4da',
                      borderRadius: '8px',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  >
                    {SUCURSALES_DISPONIBLES.map((sucursal, index) => (
                      <option key={index} value={sucursal}>{sucursal}</option>
                    ))}
                  </select>
                  
                  <input
                    type="text"
                    placeholder="Número de Mesa (ej: 1, A1, VIP-1)"
                    value={nuevaMesa.numero}
                    onChange={(e) => setNuevaMesa({ ...nuevaMesa, numero: e.target.value })}
                    required
                    style={{
                      padding: getResponsiveSize('12px', '15px'),
                      fontSize: getResponsiveSize('14px', '16px'),
                      border: '1px solid #ced4da',
                      borderRadius: '8px',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr',
                  gap: '15px', 
                  marginBottom: '15px' 
                }}>
                  <input
                    type="number"
                    placeholder="Capacidad (personas)"
                    value={nuevaMesa.capacidad}
                    onChange={(e) => setNuevaMesa({ ...nuevaMesa, capacidad: e.target.value })}
                    required
                    min="1"
                    style={{
                      padding: getResponsiveSize('12px', '15px'),
                      fontSize: getResponsiveSize('14px', '16px'),
                      border: '1px solid #ced4da',
                      borderRadius: '8px',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  />
                  
                  <select
                    value={nuevaMesa.estado}
                    onChange={(e) => setNuevaMesa({ ...nuevaMesa, estado: e.target.value })}
                    required
                    style={{
                      padding: getResponsiveSize('12px', '15px'),
                      fontSize: getResponsiveSize('14px', '16px'),
                      border: '1px solid #ced4da',
                      borderRadius: '8px',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="disponible">✅ Disponible</option>
                    <option value="ocupada">🔴 Ocupada</option>
                    <option value="reservada">🟡 Reservada</option>
                    <option value="mantenimiento">🔧 En Mantenimiento</option>
                  </select>
                </div>
                
                <button 
                  type="submit"
                  style={{
                    padding: getResponsiveSize('14px', '16px'),
                    fontSize: getResponsiveSize('16px', '18px'),
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    width: '100%',
                    fontWeight: 'bold',
                    transition: 'background-color 0.3s',
                    boxShadow: '0 2px 4px rgba(40, 167, 69, 0.3)'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#218838'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
                >
                  ✅ Agregar Mesa
                </button>
              </form>
            </div>

            {/* Lista de mesas por sucursal con diseño unificado */}
            {SUCURSALES_DISPONIBLES.map(sucursal => (
              <div key={sucursal} style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                border: '1px solid #dee2e6',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                marginBottom: '25px'
              }}>
                <div style={{
                  padding: window.innerWidth < 768 ? '15px' : '20px',
                  backgroundColor: '#f8f9fa',
                  borderBottom: '2px solid #dee2e6'
                }}>
                  <h4 style={{ 
                    margin: 0, 
                    color: '#007bff',
                    fontSize: window.innerWidth < 768 ? '16px' : '18px'
                  }}>
                    📍 {sucursal} ({(mesasPorSucursal[sucursal] || []).length} mesas)
                  </h4>
                </div>
                
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: window.innerWidth < 768 
                    ? '1fr'
                    : 'repeat(auto-fill, minmax(320px, 1fr))',
                  gap: window.innerWidth < 768 ? '0' : '20px',
                  padding: window.innerWidth < 768 ? '0' : '20px'
                }}>
                  {(mesasPorSucursal[sucursal] || []).length > 0 ? (
                    mesasPorSucursal[sucursal].map((mesa) => (
                      <div key={mesa.id} style={{
                        backgroundColor: window.innerWidth < 768 ? 'white' : '#f8f9fa',
                        border: window.innerWidth < 768 ? 'none' : '1px solid #dee2e6',
                        borderBottom: window.innerWidth < 768 ? '1px solid #dee2e6' : 'none',
                        borderRadius: window.innerWidth < 768 ? '0' : '8px',
                        padding: window.innerWidth < 768 ? '16px' : '20px',
                        transition: 'all 0.2s ease',
                        minHeight: window.innerWidth < 768 ? 'auto' : '140px',
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                        {editandoMesa?.id === mesa.id ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <h5 style={{ 
                              margin: '0 0 15px 0', 
                              color: '#007bff',
                              fontSize: getResponsiveSize('14px', '16px')
                            }}>
                              ✏️ Editando Mesa
                            </h5>
                            
                            <div style={{ 
                              display: 'grid', 
                              gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr',
                              gap: '15px'
                            }}>
                              <input
                                type="text"
                                value={editandoMesa.numero}
                                onChange={(e) => setEditandoMesa({...editandoMesa, numero: e.target.value})}
                                placeholder="Número de mesa"
                                style={{
                                  padding: getResponsiveSize('12px', '15px'),
                                  fontSize: getResponsiveSize('14px', '16px'),
                                  border: '1px solid #ced4da',
                                  borderRadius: '8px',
                                  width: '100%',
                                  boxSizing: 'border-box'
                                }}
                              />
                              <input
                                type="number"
                                value={editandoMesa.capacidad}
                                onChange={(e) => setEditandoMesa({...editandoMesa, capacidad: e.target.value})}
                                placeholder="Capacidad"
                                min="1"
                                style={{
                                  padding: getResponsiveSize('12px', '15px'),
                                  fontSize: getResponsiveSize('14px', '16px'),
                                  border: '1px solid #ced4da',
                                  borderRadius: '8px',
                                  width: '100%',
                                  boxSizing: 'border-box'
                                }}
                              />
                            </div>
                            
                            <div style={{ 
                              display: 'grid', 
                              gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr',
                              gap: '15px'
                            }}>
                              <select
                                value={editandoMesa.sucursal}
                                onChange={(e) => setEditandoMesa({...editandoMesa, sucursal: e.target.value})}
                                style={{ 
                                  padding: getResponsiveSize('12px', '15px'),
                                  fontSize: getResponsiveSize('14px', '16px'),
                                  border: '1px solid #ced4da',
                                  borderRadius: '8px',
                                  width: '100%',
                                  boxSizing: 'border-box'
                                }}
                              >
                                {SUCURSALES_DISPONIBLES.map((suc, index) => (
                                  <option key={index} value={suc}>{suc}</option>
                                ))}
                              </select>
                              
                              <select
                                value={editandoMesa.estado}
                                onChange={(e) => setEditandoMesa({...editandoMesa, estado: e.target.value})}
                                style={{ 
                                  padding: getResponsiveSize('12px', '15px'),
                                  fontSize: getResponsiveSize('14px', '16px'),
                                  border: '1px solid #ced4da',
                                  borderRadius: '8px',
                                  width: '100%',
                                  boxSizing: 'border-box'
                                }}
                              >
                                <option value="disponible">✅ Disponible</option>
                                <option value="ocupada">🔴 Ocupada</option>
                                <option value="reservada">🟡 Reservada</option>
                                <option value="mantenimiento">🔧 En Mantenimiento</option>
                              </select>
                            </div>
                            
                            <div style={{ 
                              display: 'grid', 
                              gridTemplateColumns: '1fr 1fr',
                              gap: '10px' 
                            }}>
                              <button 
                                onClick={guardarEdicionMesa}
                                style={{
                                  padding: getResponsiveSize('12px', '15px'),
                                  fontSize: getResponsiveSize('14px', '16px'),
                                  backgroundColor: '#28a745',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontWeight: 'bold',
                                  transition: 'background-color 0.3s'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#218838'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
                              >
                                ✅ Guardar
                              </button>
                              <button 
                                onClick={() => setEditandoMesa(null)}
                                style={{
                                  padding: getResponsiveSize('12px', '15px'),
                                  fontSize: getResponsiveSize('14px', '16px'),
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontWeight: 'bold',
                                  transition: 'background-color 0.3s'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c82333'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
                              >
                                ❌ Cancelar
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: '12px',
                            height: '100%'
                          }}>
                            {/* Información de la mesa */}
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '15px',
                              flex: '1'
                            }}>
                              <div style={{
                                width: window.innerWidth < 768 ? '50px' : '60px',
                                height: window.innerWidth < 768 ? '50px' : '60px',
                                backgroundColor: mesa.estado === 'disponible' ? '#28a745' : 
                                               mesa.estado === 'ocupada' ? '#dc3545' :
                                               mesa.estado === 'reservada' ? '#ffc107' : '#6c757d',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: window.innerWidth < 768 ? '20px' : '24px',
                                color: 'white',
                                fontWeight: 'bold',
                                flexShrink: 0,
                                border: '3px solid #007bff',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                              }}>
                                🪑
                              </div>
                              
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <h5 style={{
                                  margin: '0 0 8px 0',
                                  fontSize: getResponsiveSize('15px', '16px'),
                                  fontWeight: 'bold',
                                  color: '#333',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  🪑 Mesa {mesa.numero}
                                </h5>
                                
                                <div style={{ 
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '6px'
                                }}>
                                  <span style={{ 
                                    padding: '4px 10px',
                                    borderRadius: '12px',
                                    backgroundColor: mesa.estado === 'disponible' ? '#28a745' : 
                                                   mesa.estado === 'ocupada' ? '#dc3545' :
                                                   mesa.estado === 'reservada' ? '#ffc107' : '#6c757d',
                                    color: mesa.estado === 'reservada' ? '#000' : '#fff',
                                    fontSize: getResponsiveSize('11px', '12px'),
                                    fontWeight: 'bold',
                                    display: 'inline-block',
                                    alignSelf: 'flex-start',
                                    maxWidth: '100%',
                                    textAlign: 'center'
                                  }}>
                                    {mesa.estado === 'disponible' ? '✅ Disponible' : 
                                     mesa.estado === 'ocupada' ? '🔴 Ocupada' :
                                     mesa.estado === 'reservada' ? '🟡 Reservada' : '🔧 Mantenimiento'}
                                  </span>
                                  
                                  <span style={{
                                    padding: '4px 10px',
                                    borderRadius: '12px',
                                    backgroundColor: '#e7f3ff',
                                    color: '#0066cc',
                                    fontSize: getResponsiveSize('11px', '12px'),
                                    fontWeight: '500',
                                    display: 'inline-block',
                                    alignSelf: 'flex-start',
                                    maxWidth: '100%',
                                    textAlign: 'center'
                                  }}>
                                    👥 {mesa.capacidad} personas
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Botones de acción */}
                            <div style={{ 
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: '8px',
                              marginTop: 'auto'
                            }}>
                              <button 
                                onClick={() => iniciarEdicionMesa(mesa)}
                                style={{
                                  padding: getResponsiveSize('8px', '10px'),
                                  backgroundColor: '#007bff',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: getResponsiveSize('12px', '13px'),
                                  fontWeight: '500',
                                  transition: 'background-color 0.2s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '4px'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0056b3'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#007bff'}
                              >
                                <span>✏️</span>
                                <span>Editar</span>
                              </button>
                              <button 
                                onClick={() => eliminarMesa(mesa)}
                                style={{
                                  padding: getResponsiveSize('8px', '10px'),
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: getResponsiveSize('12px', '13px'),
                                  fontWeight: '500',
                                  transition: 'background-color 0.2s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '4px'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c82333'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
                              >
                                <span>🗑️</span>
                                <span>Eliminar</span>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div style={{
                      padding: window.innerWidth < 768 ? '30px 20px' : '40px 20px',
                      textAlign: 'center',
                      color: '#666',
                      backgroundColor: window.innerWidth < 768 ? 'white' : '#f8f9fa',
                      borderRadius: window.innerWidth < 768 ? '0' : '8px',
                      border: window.innerWidth < 768 ? 'none' : '1px solid #dee2e6',
                      borderBottom: window.innerWidth < 768 ? '1px solid #dee2e6' : 'none'
                    }}>
                      <h5 style={{ 
                        margin: '0 0 10px 0', 
                        fontSize: getResponsiveSize('16px', '18px'),
                        color: '#495057'
                      }}>
                        No hay mesas registradas
                      </h5>
                      <p style={{ 
                        margin: '0', 
                        fontSize: getResponsiveSize('14px', '16px'),
                        color: '#6c757d'
                      }}>
                        Esta sucursal aún no tiene mesas configuradas
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </section>
        );

      case 'comisiones':
        return (
          <section style={{ padding: window.innerWidth < 768 ? '15px' : '20px' }}>
            {/* Header moderno */}
            <div style={{
              marginBottom: '25px',
              textAlign: 'center'
            }}>
              <h2 style={{ 
                fontSize: window.innerWidth < 768 ? '24px' : '28px',
                margin: '0 0 10px 0',
                color: '#333',
                fontWeight: 'bold'
              }}>
                💰 Gestión de Comisiones Interlócales
              </h2>
              <p style={{
                fontSize: window.innerWidth < 768 ? '14px' : '16px',
                color: '#666',
                margin: '0',
                fontStyle: 'italic'
              }}>
                Administra las comisiones entre sucursales
              </p>
            </div>

            {/* Filtros modernos de fecha */}
            <div style={{
              backgroundColor: 'white',
              border: '2px solid #007bff',
              borderRadius: '16px',
              padding: window.innerWidth < 768 ? '20px' : '25px',
              marginBottom: '30px',
              boxShadow: '0 8px 20px rgba(0, 123, 255, 0.15)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '20px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: '#007bff',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px'
                }}>
                  📅
                </div>
                <h3 style={{
                  margin: '0',
                  fontSize: window.innerWidth < 768 ? '18px' : '20px',
                  color: '#333',
                  fontWeight: 'bold'
                }}>
                  Filtrar por Período
                </h3>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(3, 1fr)',
                gap: window.innerWidth < 768 ? '15px' : '20px',
                alignItems: 'end'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 'bold',
                    color: '#333',
                    fontSize: window.innerWidth < 768 ? '14px' : '15px'
                  }}>
                    📅 Fecha Desde:
                  </label>
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                    style={{
                      padding: window.innerWidth < 768 ? '12px' : '14px',
                      fontSize: window.innerWidth < 768 ? '14px' : '16px',
                      border: '2px solid #dee2e6',
                      borderRadius: '8px',
                      width: '100%',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: '#f8f9fa'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#007bff'}
                    onBlur={(e) => e.target.style.borderColor = '#dee2e6'}
                  />
                </div>
                
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 'bold',
                    color: '#333',
                    fontSize: window.innerWidth < 768 ? '14px' : '15px'
                  }}>
                    📅 Fecha Hasta:
                  </label>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                    style={{
                      padding: window.innerWidth < 768 ? '12px' : '14px',
                      fontSize: window.innerWidth < 768 ? '14px' : '16px',
                      border: '2px solid #dee2e6',
                      borderRadius: '8px',
                      width: '100%',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: '#f8f9fa'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#007bff'}
                    onBlur={(e) => e.target.style.borderColor = '#dee2e6'}
                  />
                </div>
                
                <button
                  onClick={() => cargarVentasInterlocales()}
                  style={{
                    padding: window.innerWidth < 768 ? '14px 20px' : '16px 24px',
                    fontSize: window.innerWidth < 768 ? '16px' : '18px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#218838';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(40, 167, 69, 0.4)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#28a745';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(40, 167, 69, 0.3)';
                  }}
                >
                  🔄 Actualizar Comisiones
                </button>
              </div>
            </div>
            
            {/* Resumen de comisiones */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '20px',
              marginBottom: '30px'
            }}>
              {Object.entries(reporteComisiones).length === 0 ? (
                <div style={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  color: '#6c757d',
                  fontStyle: 'italic',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '12px',
                  border: '2px dashed #dee2e6',
                  gridColumn: '1 / -1'
                }}>
                  <div style={{
                    fontSize: '48px',
                    marginBottom: '15px'
                  }}>💰</div>
                  <h4 style={{ margin: '0 0 10px 0', color: '#495057' }}>
                    No hay comisiones en el período seleccionado
                  </h4>
                  <p style={{ margin: '0', fontSize: '14px' }}>
                    Ajusta las fechas para ver las comisiones correspondientes
                  </p>
                </div>
              ) : (
                Object.entries(reporteComisiones).map(([sucursal, data]) => (
                  <div key={sucursal} style={{
                    padding: '25px',
                    backgroundColor: 'white',
                    borderRadius: '16px',
                    border: '1px solid #dee2e6',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                  }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '20px'
                    }}>
                      <div style={{
                        width: '35px',
                        height: '35px',
                        backgroundColor: '#007bff',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px'
                      }}>
                        🏢
                      </div>
                      <h4 style={{ 
                        margin: '0',
                        fontSize: window.innerWidth < 768 ? '18px' : '20px',
                        color: '#333',
                        fontWeight: 'bold'
                      }}>
                        {sucursal}
                      </h4>
                    </div>
                    
                    <div style={{ marginBottom: '15px' }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '8px'
                      }}>
                        <span style={{ fontSize: '16px' }}>💸</span>
                        <span style={{ color: '#dc3545', fontWeight: 'bold', fontSize: '16px' }}>
                          Debe pagar: ${formatNumber(data.debe || 0)}
                        </span>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{ fontSize: '16px' }}>💵</span>
                        <span style={{ color: '#28a745', fontWeight: 'bold', fontSize: '16px' }}>
                          Debe recibir: ${formatNumber(data.recibe || 0)}
                        </span>
                      </div>
                    </div>
                    
                    <div style={{ 
                      padding: '15px',
                      backgroundColor: (data.debe || 0) > (data.recibe || 0) ? '#fff5f5' : '#f0fff4',
                      borderRadius: '10px',
                      textAlign: 'center',
                      border: `2px solid ${(data.debe || 0) > (data.recibe || 0) ? '#fed7d7' : '#c6f6d5'}`
                    }}>
                      <span style={{
                        fontSize: '14px',
                        color: '#666',
                        textTransform: 'uppercase',
                        fontWeight: 'bold',
                        letterSpacing: '0.5px'
                      }}>
                        Balance Final
                      </span>
                      <div style={{
                        fontSize: window.innerWidth < 768 ? '20px' : '24px',
                        fontWeight: 'bold',
                        color: (data.debe || 0) > (data.recibe || 0) ? '#dc3545' : '#28a745',
                        marginTop: '5px'
                      }}>
                        ${formatNumber(Math.abs((data.recibe || 0) - (data.debe || 0)))}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#666',
                        marginTop: '2px'
                      }}>
                        {(data.debe || 0) > (data.recibe || 0) ? 'A pagar' : 'A recibir'}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Historial de ventas interlócales moderno */}
            <div style={{ marginTop: '30px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '25px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: '#007bff',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px'
                }}>
                  📊
                </div>
                <h3 style={{
                  margin: '0',
                  fontSize: window.innerWidth < 768 ? '20px' : '24px',
                  color: '#333',
                  fontWeight: 'bold'
                }}>
                  Historial de Ventas Interlócales
                </h3>
              </div>
              
              <div style={{ 
                overflowX: 'auto',
                border: '2px solid #007bff',
                borderRadius: '16px',
                backgroundColor: 'white',
                boxShadow: '0 8px 20px rgba(0, 123, 255, 0.15)'
              }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  marginTop: '0',
                  minWidth: window.innerWidth < 768 ? '800px' : '100%'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #007bff' }}>
                      <th style={{ 
                        padding: window.innerWidth < 768 ? '12px 8px' : '16px 12px', 
                        textAlign: 'left', 
                        border: '1px solid #dee2e6',
                        color: '#333',
                        fontSize: window.innerWidth < 768 ? '13px' : '15px',
                        fontWeight: 'bold'
                      }}>📅 Fecha</th>
                      <th style={{ 
                        padding: window.innerWidth < 768 ? '12px 8px' : '16px 12px', 
                        textAlign: 'left', 
                        border: '1px solid #dee2e6',
                        color: '#333',
                        fontSize: window.innerWidth < 768 ? '13px' : '15px',
                        fontWeight: 'bold'
                      }}>🛍️ Producto</th>
                      <th style={{ 
                        padding: window.innerWidth < 768 ? '12px 8px' : '16px 12px', 
                        textAlign: 'left', 
                        border: '1px solid #dee2e6',
                        color: '#333',
                        fontSize: window.innerWidth < 768 ? '13px' : '15px',
                        fontWeight: 'bold'
                      }}>🏪 Sucursal Venta</th>
                      <th style={{ 
                        padding: window.innerWidth < 768 ? '12px 8px' : '16px 12px', 
                        textAlign: 'left', 
                        border: '1px solid #dee2e6',
                        color: '#333',
                        fontSize: window.innerWidth < 768 ? '13px' : '15px',
                        fontWeight: 'bold'
                      }}>🏢 Sucursal Producto</th>
                      <th style={{ 
                        padding: window.innerWidth < 768 ? '12px 8px' : '16px 12px', 
                        textAlign: 'left', 
                        border: '1px solid #dee2e6',
                        color: '#333',
                        fontSize: window.innerWidth < 768 ? '13px' : '15px',
                        fontWeight: 'bold'
                      }}>💰 Total Venta</th>
                      <th style={{ 
                        padding: window.innerWidth < 768 ? '12px 8px' : '16px 12px', 
                        textAlign: 'left', 
                        border: '1px solid #dee2e6',
                        color: '#333',
                        fontSize: window.innerWidth < 768 ? '13px' : '15px',
                        fontWeight: 'bold'
                      }}>💵 Comisión</th>
                      <th style={{ 
                        padding: window.innerWidth < 768 ? '12px 8px' : '16px 12px', 
                        textAlign: 'left', 
                        border: '1px solid #dee2e6',
                        color: '#333',
                        fontSize: window.innerWidth < 768 ? '13px' : '15px',
                        fontWeight: 'bold'
                      }}>🔄 Estado</th>
                      <th style={{ 
                        padding: window.innerWidth < 768 ? '12px 8px' : '16px 12px', 
                        textAlign: 'left', 
                        border: '1px solid #dee2e6',
                        color: '#333',
                        fontSize: window.innerWidth < 768 ? '13px' : '15px',
                        fontWeight: 'bold'
                      }}>⚡ Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ventasInterlocales.length === 0 ? (
                      <tr>
                        <td colSpan="8" style={{ 
                          padding: '40px 20px', 
                          textAlign: 'center', 
                          color: '#666',
                          fontStyle: 'italic',
                          fontSize: window.innerWidth < 768 ? '14px' : '16px',
                          border: '1px solid #dee2e6'
                        }}>
                          <div style={{ marginBottom: '15px', fontSize: '48px' }}>📋</div>
                          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                            No hay ventas interlócales en el período seleccionado
                          </div>
                          <div style={{ fontSize: '14px', color: '#999' }}>
                            Ajusta las fechas del filtro para ver las transacciones correspondientes
                          </div>
                        </td>
                      </tr>
                    ) : (
                      ventasInterlocales.map(venta => (
                        <tr key={venta.id} style={{ 
                          transition: 'background-color 0.3s ease',
                          ':hover': { backgroundColor: '#f8f9fa' }
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <td style={{ 
                            padding: window.innerWidth < 768 ? '10px 8px' : '12px', 
                            border: '1px solid #dee2e6',
                            fontSize: window.innerWidth < 768 ? '12px' : '14px',
                            color: '#666'
                          }}>
                            {venta.fecha?.toLocaleDateString() || 'Fecha no disponible'}
                          </td>
                          <td style={{ 
                            padding: window.innerWidth < 768 ? '10px 8px' : '12px', 
                            border: '1px solid #dee2e6',
                            fontSize: window.innerWidth < 768 ? '12px' : '14px',
                            color: '#333',
                            fontWeight: '500'
                          }}>
                            {venta.productos?.[0]?.productoNombre || 'Producto desconocido'}
                          </td>
                          <td style={{ 
                            padding: window.innerWidth < 768 ? '10px 8px' : '12px', 
                            border: '1px solid #dee2e6',
                            fontSize: window.innerWidth < 768 ? '12px' : '14px',
                            color: '#666'
                          }}>
                            {venta.sucursalVendedora || 'N/A'}
                          </td>
                          <td style={{ 
                            padding: window.innerWidth < 768 ? '10px 8px' : '12px', 
                            border: '1px solid #dee2e6',
                            fontSize: window.innerWidth < 768 ? '12px' : '14px',
                            color: '#666'
                          }}>
                            {venta.sucursalAcreedora || 'N/A'}
                          </td>
                          <td style={{ 
                            padding: window.innerWidth < 768 ? '10px 8px' : '12px', 
                            border: '1px solid #dee2e6',
                            fontSize: window.innerWidth < 768 ? '12px' : '14px',
                            color: '#007bff',
                            fontWeight: 'bold'
                          }}>
                            ${formatNumber(venta.productos?.[0]?.subtotal || 0)}
                          </td>
                          <td style={{ 
                            padding: window.innerWidth < 768 ? '10px 8px' : '12px', 
                            border: '1px solid #dee2e6',
                            fontSize: window.innerWidth < 768 ? '12px' : '14px',
                            color: '#28a745',
                            fontWeight: 'bold'
                          }}>
                            ${formatNumber(venta.totalComision || 0)}
                          </td>
                          <td style={{ 
                            padding: window.innerWidth < 768 ? '10px 8px' : '12px', 
                            border: '1px solid #dee2e6'
                          }}>
                            <span style={{
                              padding: window.innerWidth < 768 ? '4px 8px' : '6px 12px',
                              borderRadius: '20px',
                              fontSize: window.innerWidth < 768 ? '11px' : '12px',
                              backgroundColor: venta.estado === 'pagada' ? '#d4edda' : '#fff3cd',
                              color: venta.estado === 'pagada' ? '#155724' : '#856404',
                              fontWeight: 'bold',
                              display: 'inline-block',
                              minWidth: '70px',
                              textAlign: 'center'
                            }}>
                              {venta.estado === 'pagada' ? '✅ Pagada' : '⏰ Pendiente'}
                            </span>
                          </td>
                          <td style={{ 
                            padding: window.innerWidth < 768 ? '10px 8px' : '12px', 
                            border: '1px solid #dee2e6'
                          }}>
                            {venta.estado === 'pendiente' && (
                              <button
                                onClick={() => marcarComisionPagada(venta.id)}
                                style={{
                                  backgroundColor: '#007bff',
                                  color: 'white',
                                  border: 'none',
                                  padding: window.innerWidth < 768 ? '6px 10px' : '8px 12px',
                                  borderRadius: '8px',
                                  fontSize: window.innerWidth < 768 ? '11px' : '12px',
                                  cursor: 'pointer',
                                  fontWeight: 'bold',
                                  transition: 'all 0.3s ease',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.backgroundColor = '#0056b3';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.backgroundColor = '#007bff';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                💰 Marcar Pagada
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        );

      default:
        return null;
    }
  };

  const handleImageError = (e) => {
    e.target.onerror = null; // Prevent infinite loop
    e.target.src = DEFAULT_PROFILE_IMAGE;
  };

  return (
    <>
      {/* Toast notifications */}
      {toast.visible && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={closeToast} 
          duration={3000} 
        />
      )}
      
      <div style={{ 
        display: 'flex',
        flexDirection: isMobile() ? 'column' : 'row',
        height: '100vh'
      }}>
        {/* Barra superior móvil */}
        {isMobile() && (
          <div style={{
            padding: '16px',
            backgroundColor: '#007bff',
            borderBottom: '2px solid #0056b3',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1003,
            boxShadow: '0 2px 8px rgba(0, 123, 255, 0.3)',
            minHeight: '48px'
          }}>
            <button
              onClick={() => setMenuMovilAbierto(!menuMovilAbierto)}
              style={{
                backgroundColor: menuMovilAbierto ? 'white' : 'transparent',
                border: '2px solid white',
                borderRadius: '8px',
                padding: '8px',
                cursor: 'pointer',
                color: menuMovilAbierto ? '#007bff' : 'white',
                fontSize: '16px',
                transition: 'all 0.2s ease',
                zIndex: 1004
              }}
            >
              {menuMovilAbierto ? '✕' : '☰'}
            </button>
            
            <h1 style={{
              margin: 0,
              fontSize: '18px',
              color: 'white',
              fontWeight: 'bold'
            }}>
              👤 Admin Dashboard
            </h1>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <img
                src={userProfile.imagenURL || DEFAULT_PROFILE_IMAGE}
                onError={handleImageError}
                alt="Perfil"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  border: '2px solid white',
                  objectFit: 'cover'
                }}
              />
              <span style={{
                fontSize: '12px',
                color: 'white',
                fontWeight: '500'
              }}>
                {userProfile.nombre}
              </span>
            </div>
          </div>
        )}

        {/* Overlay para cerrar menú */}
        {isMobile() && menuMovilAbierto && (
          <div
            onClick={() => setMenuMovilAbierto(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: '280px', // Solo cubre el área fuera del menú
              width: 'calc(100vw - 280px)',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              zIndex: 1001
            }}
          />
        )}

        {/* Menú móvil desplegable */}
        {isMobile() && menuMovilAbierto && (
          <div style={{
            position: 'fixed',
            top: '64px',
            left: 0,
            width: '280px',
            height: 'calc(100vh - 64px)',
            backgroundColor: '#f8f9fa',
            borderRight: '2px solid #dee2e6',
            boxShadow: '4px 0 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1002,
            overflowY: 'auto',
            padding: '20px',
            transform: 'translateX(0)',
            transition: 'transform 0.3s ease'
          }}>
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{
                margin: '0 0 15px 0',
                fontSize: '16px',
                color: '#333',
                borderBottom: '2px solid #dee2e6',
                paddingBottom: '8px'
              }}>
                📋 Navegación
              </h3>
              
              {[
                { id: 'inventario', label: 'Gestión de Inventario', icon: '📦' },
                { id: 'categorias', label: 'Gestión de Categorías', icon: '🏷️' },
                { id: 'usuarios', label: 'Gestión de Usuarios', icon: '👥' },
                { id: 'ventas', label: 'Panel de Ventas', icon: '📊' },
                { id: 'mesas', label: 'Gestión de Mesas', icon: '🪑' },
                { id: 'comisiones', label: 'Comisiones Interlócales', icon: '💰' }
              ].map(item => (
                <button
                  key={item.id}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSeccionActiva(item.id);
                    setMenuMovilAbierto(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    margin: '4px 0',
                    backgroundColor: seccionActiva === item.id ? '#007bff' : 'transparent',
                    color: seccionActiva === item.id ? 'white' : '#333',
                    border: seccionActiva === item.id ? 'none' : '1px solid #dee2e6',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    textAlign: 'left',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                >
                  <span style={{ minWidth: '20px', textAlign: 'center' }}>{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
            
            <div style={{
              borderTop: '2px solid #dee2e6',
              paddingTop: '15px',
              marginTop: '20px'
            }}>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleLogout();
                  setMenuMovilAbierto(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
              >
                🚪 Cerrar Sesión
              </button>
            </div>
          </div>
        )}

        {/* Barra lateral */}
        <div style={{
          width: getResponsiveSize('80%', '250px'),
          height: '100vh',
          backgroundColor: '#f8f9fa',
          borderRight: '2px solid #dee2e6',
          position: 'fixed',
          left: isMobile() ? '-100%' : '0',
          top: 0,
          transition: 'left 0.3s ease',
          zIndex: 1000,
          display: isMobile() ? 'none' : 'flex',
          flexDirection: 'column',
          boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)'
        }}>
          {/* Sección superior fija */}
          <div style={{
            padding: getResponsiveSize('15px', '20px'),
            borderBottom: '2px solid #dee2e6',
            backgroundColor: '#f8f9fa'
          }}>
            {/* Profile section */}
            <div style={{
              padding: '15px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
              border: '2px solid #dee2e6'
            }}>
              <div style={{ 
                width: getResponsiveSize('60px', '80px'),
                height: getResponsiveSize('60px', '80px'),
                margin: '0 auto 15px auto',
                position: 'relative'
              }}>
                <img 
                  src={userProfile.imagenURL || DEFAULT_PROFILE_IMAGE}
                  alt="User Profile"
                  onError={handleImageError}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '3px solid #007bff',
                    backgroundColor: '#f8f9fa'
                  }}
                />
              </div>
              <div style={{ textAlign: 'center' }}>
                <h3 style={{ 
                  margin: '0',
                  fontSize: '1.1rem',
                  color: '#2c3e50'
                }}>
                  {userProfile.nombre}
                </h3>
                <span style={{
                  display: 'inline-block',
                  padding: '4px 8px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '0.8rem',
                  marginTop: '5px'
                }}>
                  {userProfile.rol.charAt(0).toUpperCase() + userProfile.rol.slice(1)}
                </span>
              </div>
            </div>
            <h2 style={{ marginTop: '20px', marginBottom: '10px' }}>Panel Admin</h2>
          </div>

          {/* Sección de navegación con scroll */}
          <nav style={{ 
            padding: '20px',
            flex: 1,
            overflowY: 'auto'
          }}>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              <li style={{ marginBottom: '10px' }}>
                <button
                  onClick={() => setSeccionActiva('inventario')}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: seccionActiva === 'inventario' ? '#007bff' : '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    minHeight: '44px'
                  }}
                >
                  <span style={{ minWidth: '20px', textAlign: 'center', fontSize: '16px' }}>📦</span>
                  <span style={{ flex: 1, textAlign: 'left' }}>Gestión de Inventario</span>
                </button>
              </li>
              <li style={{ marginBottom: '10px' }}>
                <button
                  onClick={() => setSeccionActiva('categorias')}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: seccionActiva === 'categorias' ? '#007bff' : '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    minHeight: '44px'
                  }}
                >
                  <span style={{ minWidth: '20px', textAlign: 'center', fontSize: '16px' }}>🏷️</span>
                  <span style={{ flex: 1, textAlign: 'left' }}>Gestión de Categorías</span>
                </button>
              </li>
              <li style={{ marginBottom: '10px' }}>
                <button
                  onClick={() => setSeccionActiva('usuarios')}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: seccionActiva === 'usuarios' ? '#007bff' : '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    minHeight: '44px'
                  }}
                >
                  <span style={{ minWidth: '20px', textAlign: 'center', fontSize: '16px' }}>👥</span>
                  <span style={{ flex: 1, textAlign: 'left' }}>Gestión de Usuarios</span>
                </button>
              </li>
              <li style={{ marginBottom: '10px' }}>
                <button
                  onClick={() => setSeccionActiva('ventas')}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: seccionActiva === 'ventas' ? '#007bff' : '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    minHeight: '44px'
                  }}
                >
                  <span style={{ minWidth: '20px', textAlign: 'center', fontSize: '16px' }}>📊</span>
                  <span style={{ flex: 1, textAlign: 'left' }}>Panel de Ventas</span>
                </button>
              </li>
              <li style={{ marginBottom: '10px' }}>
                <button
                  onClick={() => setSeccionActiva('mesas')}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: seccionActiva === 'mesas' ? '#007bff' : '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    minHeight: '44px'
                  }}
                >
                  <span style={{ minWidth: '20px', textAlign: 'center', fontSize: '16px' }}>🪑</span>
                  <span style={{ flex: 1, textAlign: 'left' }}>Gestión de Mesas</span>
                </button>
              </li>
              <li style={{ marginBottom: '10px' }}>
                <button
                  onClick={() => setSeccionActiva('comisiones')}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: seccionActiva === 'comisiones' ? '#007bff' : '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    minHeight: '44px'
                  }}
                >
                  <span style={{ minWidth: '20px', textAlign: 'center', fontSize: '16px' }}>💰</span>
                  <span style={{ flex: 1, textAlign: 'left' }}>Comisiones Interlócales</span>
                </button>
              </li>
            </ul>
          </nav>

          {/* Sección inferior fija */}
          <div style={{
            padding: '20px',
            borderTop: '1px solid #dee2e6',
            backgroundColor: '#f8f9fa'
          }}>
            <button
              onClick={handleLogout}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c82333'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
            >
              <span>🚪</span>
              Cerrar Sesión
            </button>
          </div>
        </div>

        {/* Main content */}
        <div 
          className="dashboard-container fade-in"
          style={{ 
            marginLeft: isMobile() ? '0' : '250px',
            marginTop: isMobile() ? '80px' : '0',
            flex: 1,
            padding: getResponsiveSize('10px', '40px'),
            height: isMobile() ? 'calc(100vh - 80px)' : '100vh',
            overflowY: 'auto',
            paddingBottom: isMobile() ? '20px' : '40px',
            backgroundColor: '#ffffff',
            color: '#333',
            zIndex: 1
          }}
        >
          {renderContenido()}
        </div>
      </div>
    </>
  );
}

export default DashboardAdmin;
